import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PAYMENT_PROOF_ACCEPT,
  PAYMENT_PROOF_HINT,
  PAYMENT_PROOF_REJECT_MESSAGE,
  isAllowedPaymentProofFile,
  isPaymentProofImage,
} from '../../utils/paymentProofFile';
import api from '../../utils/api';
import API from '../../utils/apiconfig';
import DataTable from '../../components/ui/DataTable';
import ListToolbar, { ToolbarField } from '../../components/ui/ListToolbar';
import StatusBadge from '../../components/ui/StatusBadge';
import MediaPreviewModal, { handleMediaView } from '../../components/MediaPreviewModal';
import { ds } from '../../design-system';
import AppButton from '../../components/ui/AppButton';
import {
  DEFAULT_PAGE_SIZE,
  paginateClientList,
  parsePaginatedResponse,
} from '../../utils/pagination';
import {
  computeTdsBreakdown,
  computeTdsOnGrossList,
  cycleGross,
  formatInr,
} from '../../utils/withdrawalTds';
import {
  formatAppDateTime,
  formatAppMonth,
  formatPayoutCutoffDate,
  formatYearMonthLong,
  formatYearMonthTokensInText,
} from '../../utils/dateFormat';

const adminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_access_token')}`,
});

const DEFAULT_LIST_FILTERS = {
  search: '',
  status: 'yet_to_be_paid',
  bankFilter: '',
  datePreset: '',
  dateFrom: '',
  dateTo: '',
};

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function lastDayIso(year, month1to12) {
  const last = new Date(year, month1to12, 0).getDate();
  return `${year}-${String(month1to12).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

function lastDateOfCycleMonth(ymd) {
  const iso = isoDateOnly(ymd);
  const match = iso.match(/^(\d{4})-(\d{2})/);
  if (!match) return '';
  return lastDayIso(Number(match[1]), Number(match[2]));
}

function usualPreviousMonthEnd(todayIso) {
  const match = String(todayIso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return todayIso;
  let year = Number(match[1]);
  let month = Number(match[2]) - 1;
  if (month < 1) {
    month = 12;
    year -= 1;
  }
  return lastDayIso(year, month);
}

function defaultCutoffDate() {
  const today = isoToday();
  const usual = usualPreviousMonthEnd(today);
  return usual <= today ? usual : today;
}

function cycleMonthFromCutoff(ymd) {
  if (!ymd || String(ymd).length < 7) return '';
  const match = String(ymd).match(/(\d{4})-(\d{2})/);
  if (!match) return '';
  return `${match[1]}-${match[2]}-01`;
}

function isoDateOnly(value) {
  if (!value) return '';
  const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
}

function isSameCycleMonth(a, b) {
  const left = cycleMonthFromCutoff(a);
  const right = cycleMonthFromCutoff(b);
  return Boolean(left && left === right);
}

function cutoffBelongsToMonth(cutoff, cycleMonth) {
  return isSameCycleMonth(cutoff, cycleMonth);
}

/**
 * Payout status filter — payment_status on cycle payouts.
 */
const STATUS_OPTIONS = [
  { value: 'yet_to_be_paid', label: 'Yet To Be Paid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'payment_issues', label: 'Payment Issues' },
  { value: 'failed', label: 'Failed Transactions' },
  { value: 'all', label: 'All' },
];

/** Summarise one member's cycle payouts for list badges. */
function summarizeMemberPayout(cycles) {
  const list = (cycles || []).filter((c) => c.status !== 'suspended');
  const total = list.length;
  const paid = list.filter((c) => (c.payment_status || c.status) === 'paid').length;
  const partial = list.filter((c) => (c.payment_status || '') === 'partially_paid').length;
  const failed = list.filter((c) => (c.payment_status || '') === 'failed').length;
  const issues = list.filter(
    (c) =>
      (c.member_confirmation || '') === 'concern_raised' ||
      (c.open_concern && c.open_concern.id),
  ).length;
  const yet = list.filter(
    (c) => (c.payment_status || 'yet_to_be_paid') === 'yet_to_be_paid',
  ).length;

  let payCircle = 'red';
  if (total > 0 && paid === total) payCircle = 'green';
  else if (paid > 0 || partial > 0) payCircle = 'orange';

  let key = 'yet_to_be_paid';
  let label = 'Yet To Be Paid';
  let tone = 'neutral';

  if (issues > 0 && (paid + partial + failed) >= 0) {
    // Prefer payment issues when any open concern on member cycles.
    if (list.some((c) => (c.member_confirmation || '') === 'concern_raised')) {
      key = 'payment_issues';
      label = 'Payment Issues';
      tone = 'danger';
    }
  }
  if (key !== 'payment_issues') {
    if (total > 0 && failed === total) {
      key = 'failed';
      label = 'Failed';
      tone = 'danger';
    } else if (total > 0 && paid === total) {
      key = 'paid';
      label = 'Paid';
      tone = 'success';
    } else if (partial > 0 || (paid > 0 && paid < total)) {
      key = 'partially_paid';
      label = partial === total
        ? 'Partially Paid'
        : `Partially Paid (${paid}/${total})`;
      tone = 'warning';
    } else if (yet === total || total === 0) {
      key = 'yet_to_be_paid';
      label = 'Yet To Be Paid';
      tone = 'info';
    }
  }

  const totalPayable = list.reduce(
    (s, c) => s + Number(c.net_payable_amount ?? c.withdrawal_amount_inr ?? 0),
    0,
  );
  const totalPaidAmt = list.reduce((s, c) => s + Number(c.paid_amount ?? 0), 0);
  const totalRemaining = list.reduce(
    (s, c) => s + Number(c.remaining_amount ?? Math.max(0, Number(c.net_payable_amount ?? 0) - Number(c.paid_amount ?? 0))),
    0,
  );

  return {
    key,
    label,
    tone,
    payCircle,
    paid_count: paid,
    total_count: total,
    total_payable: totalPayable,
    total_paid_amount: totalPaidAmt,
    total_remaining: totalRemaining,
  };
}

/** Common clipboard / check icons for one-click copy. */
function CopyIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CopiedIcon({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** One row per member — month cycles open inside the eye popup. */
const COLUMNS = [
  { key: 'member', label: 'Member' },
  // { key: 'progress', label: 'Pay progress' },
  { key: 'payable', label: 'Total Payable' },
  { key: 'paid', label: 'Paid Amount' },
  { key: 'remaining', label: 'Remaining to Pay' },
  { key: 'status', label: 'Payment Status' },
  { key: 'bank', label: 'Bank' },
  { key: 'actions', label: 'Actions' },
];

function formatWhen(iso) {
  return formatAppDateTime(iso);
}

function formatDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 48) return `${Math.floor(h / 24)}d`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function lockedCutoffDateLabel(detail) {
  if (!detail?.cutoff_at && !detail?.cutoff_date) return '';
  return formatPayoutCutoffDate(detail.cutoff_date || detail.cutoff_at);
}

function RecordPaymentCutoffNote({ detail }) {
  const cutoffLabel = lockedCutoffDateLabel(detail);
  const leftover = Number(detail.carry_forward_points ?? 0);
  const unsettledPts = Number(detail.unsettled_gross_points ?? 0).toLocaleString('en-IN');
  const remainingInr = formatInr(detail.remaining_amount);
  const alreadySent = Number(detail.paid_amount) > 0;
  return (
    <>
      {cutoffLabel ? (
        <p className="text-sm text-ink">
          Why ₹{remainingInr} this month: cutoff <strong>{cutoffLabel}</strong> was locked for{' '}
          <strong>{formatYearMonthLong(detail.payout_month)}</strong>. Only points till that day
          are in this payout ({unsettledPts} pts
          {alreadySent
            ? `, ₹${remainingInr} still to send after 5% tax`
            : ` → ₹${remainingInr} after 5% tax`}
          ).
        </p>
      ) : null}
      {leftover > 0 ? (
        <p className="text-sm text-ink">
          ⏳ {leftover.toLocaleString('en-IN')} pts
          {cutoffLabel ? ` earned after ${cutoffLabel}. They` : ''} wait for next month. Do not add
          them in this payment.
        </p>
      ) : (
        <p className="text-sm text-ink">
          ✅ No extra points{cutoffLabel ? ` after ${cutoffLabel}` : ''}. Nothing is waiting for
          next month.
        </p>
      )}
    </>
  );
}

function walletDebitPreview(detail, enteredPay) {
  const gross = Number(detail?.earned_points ?? detail?.amount ?? 0);
  const already = Number(detail?.points_debited ?? 0);
  const remainingGross = Math.max(0, gross - already);
  const remainingNet = Number(detail?.remaining_amount ?? 0);
  const pay = Number(enteredPay);
  if (!Number.isFinite(pay) || pay <= 0 || remainingGross <= 0 || remainingNet <= 0) {
    return remainingGross > 0 && remainingNet > 0 ? remainingGross : 0;
  }
  if (pay + 0.001 >= remainingNet) return remainingGross;
  return Math.round((remainingGross * pay / remainingNet) * 100) / 100;
}

function payoutMonthLabel(iso) {
  return formatAppMonth(iso);
}

function isWithdrawalUnpaidStatus(status, row) {
  const record = row || (status && typeof status === 'object' ? status : null);
  const st = record?.status || status;
  if (st === 'suspended') return false;
  if (record && record.payment_status) {
    return ['yet_to_be_paid', 'partially_paid', 'failed', 'processing'].includes(record.payment_status)
      && Number(record.remaining_amount ?? 0) > 0;
  }
  return ['not_started', 'pending', 'approved', 'yet_to_be_paid'].includes(st);
}

function rolledIntoLabel(row, history) {
  const month =
    row?.superseded_by_payout_month ||
    history?.find((h) => h.id === row?.superseded_by_id)?.payout_month;
  if (month) return `Replaced by ${payoutMonthLabel(month)} — do not pay this`;
  return 'Replaced by a later payout — do not pay this';
}

function allocationParts(row) {
  const months = row?.payout_breakdown?.months || row?.earning_allocations || [];
  return months
    .filter((m) => Number(m.gross_points || 0) > 0 && m.earning_month)
    .map((m) => ({
      month: payoutMonthLabel(m.earning_month),
      pts: Number(m.gross_points || 0),
    }));
}

function ledgerEarnedForPayoutMonth(detail, payoutMonth) {
  const key = String(payoutMonth || '').slice(0, 7);
  if (!key) return null;
  const row = (detail?.earnings_breakdown || []).find(
    (r) => String(r.payout_month || '').slice(0, 7) === key,
  );
  if (!row) return null;
  return Number(row.earned_points || 0);
}

function monthClaimedInPayout(detail, payoutMonthIso) {
  const allocs = detail?.earning_allocations || [];
  const breakdown = detail?.payout_breakdown?.months || [];
  const listed = [...allocs, ...breakdown].filter((a) => a.earning_month);
  if (listed.length) {
    return listed.some((a) => a.earning_month === payoutMonthIso);
  }
  if (!detail?.cutoff_at || !payoutMonthIso) return false;
  const cutoff = new Date(detail.cutoff_at);
  const monthStart = new Date(`${payoutMonthIso}T00:00:00`);
  return monthStart <= cutoff;
}

function AdminEarningsByMonth({ detail }) {
  const rows = detail?.earnings_breakdown || [];
  if (!rows.length) return null;
  const cutoffLabel = detail.cutoff_at ? formatPayoutCutoffDate(detail.cutoff_at) : null;
  return (
    <div className="rounded-ds-lg border-2 border-amber-300 bg-amber-50/40 p-3 space-y-2">
      <p className="text-sm font-bold text-ink">Where each month’s points went</p>
      <p className="text-xs text-ink">
        This is the real earnings ledger. It is not the old draft cards below.
        {cutoffLabel ? (
          <>
            {' '}This payout only counts points till <strong>{cutoffLabel}</strong>.
          </>
        ) : null}
      </p>
      <ul className="divide-y divide-amber-100 rounded-ds-lg border border-amber-200 bg-white">
        {rows.map((row) => {
          const pts = Number(row.earned_points || 0);
          const inThisPay = monthClaimedInPayout(detail, row.payout_month);
          return (
            <li
              key={row.payout_month}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
            >
              <div>
                <p className="text-sm font-bold text-ink">{payoutMonthLabel(row.payout_month)}</p>
                <p className="text-lg font-black tabular-nums text-ink">
                  {pts.toLocaleString('en-IN')} pts earned
                </p>
              </div>
              {inThisPay ? (
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-900">
                  In this payout — pay now
                </span>
              ) : (
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-900">
                  Waiting — next cutoff
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-ink">
        <strong>Waiting</strong> months are leftover points after this cutoff.
        Do not add them to the green ₹ amount.
      </p>
    </div>
  );
}

function MemberMonthRow({
  h,
  detail,
  memberHistory,
  openDetail,
  setDetailTab,
  setMedia,
}) {
  const unpaid = isWithdrawalUnpaidStatus(h.status, h);
  const suspended = h.status === 'suspended';
  const isActive = h.id === detail.id;
  const earnedPts = Number(h.earned_points || h.amount || cycleGross(h) || 0);
  const monthTds = computeTdsBreakdown(cycleGross(h) || earnedPts);
  const stillToPayNet = Number(
    h.net_payable_amount
      ?? h.withdrawal_amount_inr
      ?? monthTds.net,
  );
  const parts = allocationParts(h);
  const ownMonthPts = ledgerEarnedForPayoutMonth(detail, h.payout_month);
  return (
    <li
      className={`rounded-ds-xl border p-3 text-sm ${
        suspended
          ? 'border-line bg-slate-50 text-ink-muted'
          : isActive
            ? 'border-brand-200 bg-brand-50'
            : ['raised_concern', 'under_review'].includes(h.status)
              ? 'border-danger-100 bg-danger-50'
              : 'border-line'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className={`font-semibold ${suspended ? 'text-ink-muted' : 'text-ink'}`}>
            {payoutMonthLabel(h.payout_month)}
            <span className="text-[10px] font-medium text-ink-muted">
              {' '}#{h.cycle_no || 1}
            </span>
          </p>
          {suspended ? (
            <div className="mt-2 space-y-2 text-sm text-ink">
              <p className="font-bold">Do not pay the {earnedPts.toLocaleString('en-IN')} pts on this card.</p>
              {parts.length > 0 ? (
                <div className="rounded-ds-lg border border-line bg-white px-3 py-2">
                  <p className="text-[11px] font-bold uppercase text-ink-muted mb-1">
                    Why this card showed {earnedPts.toLocaleString('en-IN')}
                  </p>
                  <ul className="space-y-1">
                    {parts.map((p) => (
                      <li key={p.month} className="flex justify-between gap-3 tabular-nums">
                        <span>
                          {p.month}
                          {p.month === payoutMonthLabel(h.payout_month)
                            ? ' — earned in this month'
                            : ' — unpaid from earlier, folded in'}
                        </span>
                        <span className="font-semibold">{p.pts.toLocaleString('en-IN')} pts</span>
                      </li>
                    ))}
                    <li className="flex justify-between gap-3 border-t border-line pt-1 font-bold tabular-nums">
                      <span>Old combined draft</span>
                      <span>{earnedPts.toLocaleString('en-IN')} pts</span>
                    </li>
                  </ul>
                  <p className="text-xs text-ink-muted mt-2">
                    {payoutMonthLabel(h.payout_month)} did not jump from a smaller number.
                    Older unpaid points were added on top, then this whole draft was cancelled.
                    Pay only the green ₹ above.
                  </p>
                </div>
              ) : ownMonthPts != null && ownMonthPts !== earnedPts ? (
                <p>
                  {payoutMonthLabel(h.payout_month)} earned {ownMonthPts.toLocaleString('en-IN')} pts that month.
                  The {earnedPts.toLocaleString('en-IN')} here also includes unpaid points from earlier months.
                  Do not transfer {earnedPts.toLocaleString('en-IN')}.
                </p>
              ) : (
                <p className="text-ink-muted">{rolledIntoLabel(h, memberHistory)}.</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-ink-muted mt-0.5 tabular-nums">
              {earnedPts.toLocaleString('en-IN')} pts in this cycle
              {' · '}tax 5% ₹{formatInr(h.tds_amount ?? monthTds.tds)}
              {!unpaid ? (
                <span className="ml-2 text-[11px] font-bold text-emerald-700">
                  {(h.payment_status === 'paid')
                    ? 'Done'
                    : h.status === 'awaiting_confirmation' || h.status === 'paid'
                      ? 'Paid — awaiting acknowledgement'
                      : 'Already paid'}
                </span>
              ) : (
                <span className="ml-2 text-[11px] font-bold text-amber-800">
                  Still to pay ₹{formatInr(stillToPayNet)}
                </span>
              )}
            </p>
          )}
        </div>
        <StatusBadge
          tone={suspended ? 'neutral' : (h.status_tone || 'neutral')}
          uppercase={false}
        >
          {suspended ? 'Old draft' : (h.status_label || h.status)}
        </StatusBadge>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {h.payment_proof_url ? (
          <AppButton
            type="button"
            variant="info"
            icon="eye"
            size="sm"
            onClick={() =>
              handleMediaView(h.payment_proof_url, {
                onPreview: setMedia,
                title: 'Payment proof',
                subtitle: payoutMonthLabel(h.payout_month),
              })
            }
          >
            View proof
          </AppButton>
        ) : null}
        {suspended ? (
          <>
            <AppButton
              type="button"
              variant="secondary"
              icon="open"
              size="md"
              onClick={() => {
                openDetail(h, { keepPayTogether: true });
                setDetailTab('details');
              }}
            >
              View draft
            </AppButton>
            {h.superseded_by_id || h.superseded_by_payout_month ? (
              <AppButton
                type="button"
                variant="success"
                icon="open"
                size="md"
                onClick={() => {
                  const target =
                    memberHistory.find((m) => m.id === h.superseded_by_id) ||
                    memberHistory.find(
                      (m) =>
                        m.payout_month === h.superseded_by_payout_month &&
                        m.status !== 'suspended',
                    );
                  if (target) {
                    openDetail(target, { keepPayTogether: true });
                    setDetailTab('details');
                  }
                }}
              >
                Go to live payout
              </AppButton>
            ) : null}
          </>
        ) : (
          <AppButton
            type="button"
            variant={unpaid ? 'success' : 'primary'}
            icon={unpaid ? 'pay' : 'open'}
            size="md"
            className={
              unpaid
                ? 'shadow-sm ring-1 ring-emerald-800/15 transition hover:-translate-y-0.5 hover:shadow-md active:translate-y-0'
                : 'shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:translate-y-0'
            }
            onClick={() => {
              openDetail(h, { keepPayTogether: true });
              setDetailTab('details');
            }}
          >
            {unpaid ? 'Pay this cycle' : 'Open'}
          </AppButton>
        )}
      </div>
    </li>
  );
}

function includedMonthsForCycle(detail, history) {
  const fromBreakdown = detail?.payout_breakdown?.months;
  if (fromBreakdown && fromBreakdown.length > 0) return fromBreakdown;
  const allocations = detail?.earning_allocations || [];
  if (allocations.length > 0) return allocations;
  return (history || [])
    .filter((h) => h.superseded_by_id === detail?.id)
    .map((h) => {
      const gross = cycleGross(h);
      const { tds, net } = computeTdsBreakdown(gross);
      return {
        earning_month: h.payout_month,
        gross_points: gross,
        tds_amount: tds,
        net_payable_amount: net,
        source: 'rolled_in',
      };
    });
}

function allMemberPaymentTxns(detail, history) {
  const rows = [];
  const seen = new Set();
  for (const cycle of [detail, ...(history || [])]) {
    if (!cycle) continue;
    for (const t of cycle.payment_transactions || []) {
      if (t?.id == null || seen.has(t.id)) continue;
      seen.add(t.id);
      rows.push({
        ...t,
        payout_month: cycle.payout_month,
        cycle_status: cycle.status,
      });
    }
  }
  rows.sort((a, b) => String(a.paid_at || a.created_at || '').localeCompare(String(b.paid_at || b.created_at || '')));
  return rows;
}

export default function WithdrawalsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const wdUserId = searchParams.get('wd_user');
  const showingMemberPayout = Boolean(wdUserId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  /** Toolbar inputs — changing these does not fetch or filter until Apply. */
  const [draftFilters, setDraftFilters] = useState(DEFAULT_LIST_FILTERS);
  /** Last applied filters — drive fetch + list visibility. */
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_LIST_FILTERS);
  const [detail, setDetail] = useState(null);
  const [memberHistory, setMemberHistory] = useState([]);
  const [detailTab, setDetailTab] = useState('months'); // months | details | timeline | admin
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  /** Notes textarea only after admin taps Reject (or another action that needs a reason). */
  const [showAdminNotes, setShowAdminNotes] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [proofDragActive, setProofDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState(null);
  const [showMoreHelp, setShowMoreHelp] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [finalizeCutoffDate, setFinalizeCutoffDate] = useState(() => defaultCutoffDate());
  const [cycleLockInfo, setCycleLockInfo] = useState({
    today: isoToday(),
    first_operational_cycle: '2026-08-01',
    current_cycle_month: cycleMonthFromCutoff(isoToday()),
    locked_cycle_months: [],
    locks: [],
  });
  const [finalizeModalStep, setFinalizeModalStep] = useState(0);
  const [finalizeAckChecked, setFinalizeAckChecked] = useState(false);
  const [showCutoffHelp, setShowCutoffHelp] = useState(false);
  /** Multi-month pay-together context (list opens detail without expanding month rows). */
  const [payTogetherSummary, setPayTogetherSummary] = useState(null);
  const proofInputRef = useRef(null);
  const proofPreviewUrl = useMemo(() => {
    if (!proofFile || !isPaymentProofImage(proofFile)) return '';
    return URL.createObjectURL(proofFile);
  }, [proofFile]);

  useEffect(() => {
    setFinalizeAckChecked(false);
  }, [finalizeCutoffDate]);

  useEffect(() => {
    if (!proofPreviewUrl) return undefined;
    return () => URL.revokeObjectURL(proofPreviewUrl);
  }, [proofPreviewUrl]);

  const acceptProofFile = (file) => {
    if (!file) {
      setProofFile(null);
      return;
    }
    if (!isAllowedPaymentProofFile(file)) {
      showToast(PAYMENT_PROOF_REJECT_MESSAGE);
      return;
    }
    setProofFile(file);
  };

  const showToast = (msg) => {
    setToast(msg);
  };

  const openMemberProfile = (userId) => {
    if (userId == null || userId === '') return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'members');
    next.set('member', String(userId));
    next.delete('edit');
    next.delete('wd_user');
    setSearchParams(next);
  };

  const setWdUserParam = useCallback(
    (userId) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'withdrawals');
      if (userId == null || userId === '') next.delete('wd_user');
      else next.set('wd_user', String(userId));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const resetDetailState = useCallback(() => {
    setDetail(null);
    setMemberHistory([]);
    setPayTogetherSummary(null);
    setDetailTab('months');
    setAdminNote('');
    setShowAdminNotes(false);
    setProofFile(null);
    setProofDragActive(false);
  }, []);

  const closeMemberPayoutView = useCallback(() => {
    resetDetailState();
    setWdUserParam(null);
  }, [resetDetailState, setWdUserParam]);

  const fullAccountNumber = (bank) => {
    if (!bank) return '';
    return String(bank.account_number_full || bank.account_number || '').trim();
  };

  const copyText = async (label, value) => {
    const text = String(value || '').trim();
    if (!text) {
      showToast('Nothing to copy for this field.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(label);
      showToast(`${label} copied — paste in bank app, GPay, or WhatsApp.`);
      window.setTimeout(() => setCopiedField(''), 2000);
    } catch {
      showToast('Could not copy. Select the text and copy manually.');
    }
  };

  const copyAllPayoutDetails = async (row) => {
    if (!row) return;
    const bank = row.bank_details || {};
    const { gross, tds, net } = computeTdsOnGrossList([cycleGross(row)]);
    const lines = [
      `Payee: ${bank.account_holder_name || row.member_name || row.username || ''}`.trim(),
      `Gross: ₹${formatInr(gross)}`,
      `TDS (5%): ₹${formatInr(tds)}`,
      `Net Amount Payable: ₹${formatInr(net)}`,
      bank.bank_name ? `Bank: ${bank.bank_name}` : null,
      fullAccountNumber(bank) ? `Account: ${fullAccountNumber(bank)}` : null,
      bank.ifsc_code ? `IFSC: ${bank.ifsc_code}` : null,
      bank.upi_id ? `UPI: ${bank.upi_id}` : null,
      row.phone ? `Mobile: ${row.phone}` : null,
    ].filter(Boolean);
    await copyText('Payout details', lines.join('\n'));
  };

  const readActionMessage = (payload, fallback = 'Please check the details and try again.') => {
    if (!payload) return fallback;
    const msg = payload.message || payload.error;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
    if (Array.isArray(msg)) return msg.filter(Boolean).join(' ') || fallback;
    return fallback;
  };

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    const f = {
      search: opts.search !== undefined ? opts.search : appliedFilters.search,
      status: opts.status !== undefined ? opts.status : appliedFilters.status,
      datePreset: opts.datePreset !== undefined ? opts.datePreset : appliedFilters.datePreset,
      dateFrom: opts.dateFrom !== undefined ? opts.dateFrom : appliedFilters.dateFrom,
      dateTo: opts.dateTo !== undefined ? opts.dateTo : appliedFilters.dateTo,
    };
    try {
      const fetchSize = 100;
      let pageNum = 1;
      let apiPages = 1;
      const allItems = [];
      while (pageNum <= apiPages) {
        const params = {
          page: pageNum,
          page_size: fetchSize,
          view: 'management',
          group_by_member: '1',
          all_months: '1',
          include_history: '1',
        };
        if (String(f.search || '').trim()) params.search = String(f.search).trim();
        if (f.status && f.status !== 'all') params.payment_status = f.status;
        if (f.datePreset) params.date_preset = f.datePreset;
        if (f.dateFrom) params.date_from = f.dateFrom;
        if (f.dateTo) params.date_to = f.dateTo;

        const res = await api.get(API.ADMIN_WITHDRAWALS, {
          headers: adminHeaders(),
          params,
        });
        const parsed = parsePaginatedResponse(res.data, ['results', 'withdrawals']);
        allItems.push(...parsed.items);
        apiPages = Math.max(1, Number(parsed.totalPages) || 1);
        pageNum += 1;
        if (pageNum > 50) break;
      }
      setRows(allItems);
      if (opts.resetPage) setPage(1);
      try {
        const lockRes = await api.get(API.ADMIN_WITHDRAWAL_FINALIZE_CYCLE, {
          headers: adminHeaders(),
        });
        const data = lockRes?.data || {};
        setCycleLockInfo({
          today: data.today || isoToday(),
          first_operational_cycle: data.first_operational_cycle || '2026-08-01',
          current_cycle_month: data.current_cycle_month || cycleMonthFromCutoff(isoToday()),
          locked_cycle_months: data.locked_cycle_months || [],
          locks: data.locks || [],
        });
        const today = data.today || isoToday();
        const currentMonth =
          data.current_cycle_month || cycleMonthFromCutoff(today);
        const monthLock = (data.locks || []).find((lock) =>
          isSameCycleMonth(lock.cycle_month, currentMonth),
        );
        let savedCutoff = monthLock?.cutoff_date;
        if (!cutoffBelongsToMonth(savedCutoff, currentMonth)) {
          const frozen = allItems.find(
            (row) =>
              row.status !== 'suspended' &&
              row.amount_frozen &&
              isSameCycleMonth(row.payout_month, currentMonth) &&
              cutoffBelongsToMonth(row.cutoff_date || row.cutoff_at, currentMonth),
          );
          savedCutoff = frozen?.cutoff_date || frozen?.cutoff_at || savedCutoff;
        }
        const savedYmd = isoDateOnly(savedCutoff);
        if (savedYmd && isSameCycleMonth(savedYmd, currentMonth)) {
          setFinalizeCutoffDate(savedYmd);
        } else if (!monthLock) {
          const usual = usualPreviousMonthEnd(today);
          const first = data.first_operational_cycle || '2026-08-01';
          if (usual >= first && usual <= today) {
            setFinalizeCutoffDate(usual);
          }
        }
      } catch {
        /* payout list still works if lock history is unavailable */
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to load withdrawals.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  const applyFilters = () => {
    const next = { ...draftFilters };
    setAppliedFilters(next);
    load({ ...next, resetPage: true });
  };

  const clearFilters = () => {
    const cleared = { ...DEFAULT_LIST_FILTERS };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    load({ ...cleared, resetPage: true });
  };

  const emptyListHint =
    'Change filters, then tap Apply Filters — or Clear Filters / Refresh list.';

  // Initial list only — never refetch just because a filter field changed.
  useEffect(() => {
    load({ resetPage: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional

  const openDetail = async (row, { keepPayTogether = false, keepTab = false } = {}) => {
    setDetailLoading(true);
    setAdminNote('');
    setShowAdminNotes(false);
    setProofFile(null);
    setProofDragActive(false);
    // After Mark Paid / Approve etc., stay on the same tab so admin sees the update.
    if (!keepPayTogether && !keepTab) {
      setDetailTab('months');
      setPayTogetherSummary(null);
    }
    setMemberHistory([]);
    try {
      const res = await api.get(API.ADMIN_WITHDRAWAL(row.id || row.withdrawal_id), {
        headers: adminHeaders(),
      });
      const withdrawal = res.data?.withdrawal || row;
      const history = res.data?.member_history || [];
      setDetail(withdrawal);
      setMemberHistory(history);
      const remainingNow = Number(withdrawal.remaining_amount ?? 0);
      setPayAmount(remainingNow > 0 ? String(remainingNow) : '');
      const unpaid = history.filter((h) => isWithdrawalUnpaidStatus(h.status, h));
      if (unpaid.length >= 1) {
        const consolidated = computeTdsOnGrossList(unpaid.map(cycleGross));
        setPayTogetherSummary({
          user_id: withdrawal.user_id,
          unpaid_count: unpaid.length,
          total_gross: consolidated.gross,
          total_tds: consolidated.tds,
          total_payable: consolidated.net,
          unpaid_months: unpaid,
        });
      } else {
        setPayTogetherSummary(null);
      }
    } catch {
      setDetail(row);
      setMemberHistory([]);
    } finally {
      setDetailLoading(false);
    }
  };

  /** Refresh current member payout page after an action — do not bounce to the list. */
  const refreshMemberPayoutView = async (withdrawalRow) => {
    const row = withdrawalRow || detail;
    if (!row) return;
    if (row.user_id != null) setWdUserParam(row.user_id);
    await openDetail(row, { keepTab: true });
    await load();
  };

  const memberGroups = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const uid = row.user_id;
      if (!map.has(uid)) {
        map.set(uid, {
          user_id: uid,
          member_name: row.member_name || row.username,
          username: row.username,
          phone: row.phone || '',
          user_email: row.user_email || '',
          bank_details_ok: row.bank_details_ok !== false,
          cycles: [],
        });
      }
      const g = map.get(uid);
      g.cycles.push(row);
      if (row.bank_details_ok === false) g.bank_details_ok = false;
    }
    const groups = Array.from(map.values());
    for (const g of groups) {
      g.cycles.sort((a, b) => {
        const am = a.payout_month || '';
        const bm = b.payout_month || '';
        if (am !== bm) return am.localeCompare(bm);
        return (a.created_at || '').localeCompare(b.created_at || '');
      });
      const unpaid = g.cycles.filter((c) => isWithdrawalUnpaidStatus(c.status, c));
      const consolidated = computeTdsOnGrossList(unpaid.map(cycleGross));
      g.unpaid_count = unpaid.length;
      g.total_gross = consolidated.gross;
      g.total_tds = consolidated.tds;
      g.total_payable = consolidated.net;
      g.primary = unpaid[0] || g.cycles[0];
      const summary = summarizeMemberPayout(g.cycles);
      g.member_status_key = summary.key;
      g.status_label = summary.label;
      g.status_tone = summary.tone;
      g.pay_circle = summary.payCircle;
      g.paid_count = summary.paid_count;
      g.total_count = summary.total_count;
      g.total_payable = summary.total_payable;
      g.total_paid_amount = summary.total_paid_amount;
      g.total_remaining = summary.total_remaining;
    }
    groups.sort((a, b) =>
      String(a.member_name || a.username).localeCompare(
        String(b.member_name || b.username),
        undefined,
        { sensitivity: 'base' },
      ),
    );
    return groups;
  }, [rows]);

  const visibleMemberGroups = useMemo(() => {
    let list = memberGroups;
    const { status: appliedStatus, bankFilter: appliedBank } = appliedFilters;
    if (appliedStatus && appliedStatus !== 'all') {
      list = list.filter((g) => g.member_status_key === appliedStatus);
    }
    if (appliedBank === 'updated') {
      list = list.filter((g) => g.bank_details_ok);
    } else if (appliedBank === 'missing') {
      list = list.filter((g) => !g.bank_details_ok);
    }
    return list;
  }, [memberGroups, appliedFilters]);

  const pagedMemberGroups = useMemo(
    () => paginateClientList(visibleMemberGroups, page, pageSize),
    [visibleMemberGroups, page, pageSize],
  );

  // After filters shrink the list, keep the current page in range.
  useEffect(() => {
    if (page > pagedMemberGroups.totalPages) {
      setPage(pagedMemberGroups.totalPages);
    }
  }, [page, pagedMemberGroups.totalPages]);

  const openMemberGroup = (group) => {
    if (!group?.primary) return;
    // Missing bank/UPI still allows viewing (timeline, cutoff, amounts).
    // Record payment stays blocked on the payout page.
    setWdUserParam(group.user_id);
  };

  // Clear detail when leaving the member payout page (e.g. browser Back).
  useEffect(() => {
    if (!wdUserId) resetDetailState();
  }, [wdUserId, resetDetailState]);

  // Deep-link / browser back-forward: open member payout page from ?wd_user=
  useEffect(() => {
    if (!wdUserId) return undefined;
    if (detail && String(detail.user_id) === String(wdUserId)) return undefined;
    if (loading) return undefined;

    const group = memberGroups.find((g) => String(g.user_id) === String(wdUserId));
    if (group?.primary) {
      openDetail(group.primary);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.post(
          API.ADMIN_WITHDRAWAL_USER_RECALCULATE(wdUserId),
          {},
          { headers: adminHeaders() },
        );
        if (cancelled) return;
        const list = res.data?.withdrawals || [];
        if (!list.length) {
          showToast('No payout records for this member.');
          setWdUserParam(null);
          return;
        }
        const primary =
          list.find((w) => isWithdrawalUnpaidStatus(w.status, w)) || list[0];
        openDetail(primary);
      } catch {
        if (!cancelled) {
          showToast('Unable to open member payouts.');
          setWdUserParam(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → detail only
  }, [wdUserId, memberGroups, loading]);

  const runAction = async (fn, successMsg) => {
    setSaving(true);
    try {
      const res = await fn();
      const data = res?.data;
      if (data && data.success === false) {
        showToast(readActionMessage(data));
        if (data.withdrawal) setDetail(data.withdrawal);
        return;
      }
      showToast(data?.message || successMsg);
      await refreshMemberPayoutView(data?.withdrawal || detail);
    } catch (e) {
      showToast(readActionMessage(e.response?.data, 'Please check the details and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = () => {
    if (!detail.bank_details_ok) {
      showToast(
        detail.bank_block_reason
          || 'Member must update bank / UPI details before you can Approve. Ask them via mobile or email first.'
      );
      return;
    }
    return runAction(
      () =>
        api.post(
          API.ADMIN_WITHDRAWAL_APPROVE(detail.id),
          { admin_note: adminNote },
          { headers: adminHeaders() }
        ),
      'Approved for payout (points not deducted yet)'
    );
  };

  const handleReject = () => {
    if (!showAdminNotes) {
      setShowAdminNotes(true);
      showToast('Enter rejection reason in Notes, then tap Confirm reject.');
      return;
    }
    if ((adminNote || '').trim().length < 5) {
      showToast('Rejection reason must be at least 5 characters.');
      return;
    }
    return runAction(
      () =>
        api.post(
          API.ADMIN_WITHDRAWAL_REJECT(detail.id),
          { admin_note: adminNote },
          { headers: adminHeaders() }
        ),
      'Withdrawal rejected'
    );
  };

  const selectedCycleMonth = cycleMonthFromCutoff(finalizeCutoffDate);
  const lockedMonthKeys = useMemo(() => {
    const keys = new Set();
    for (const value of cycleLockInfo.locked_cycle_months || []) {
      const key = cycleMonthFromCutoff(value);
      if (key) keys.add(key);
    }
    for (const lock of cycleLockInfo.locks || []) {
      const key = cycleMonthFromCutoff(lock.cycle_month);
      if (key) keys.add(key);
    }
    return keys;
  }, [cycleLockInfo]);
  const selectedMonthLocked = lockedMonthKeys.has(selectedCycleMonth);
  const selectedMonthLock = (cycleLockInfo.locks || []).find((lock) =>
    isSameCycleMonth(lock.cycle_month, selectedCycleMonth),
  );
  const selectedMonthCutoff = (() => {
    const lockDate = selectedMonthLock?.cutoff_date;
    if (lockDate && cutoffBelongsToMonth(lockDate, selectedCycleMonth)) {
      return lockDate;
    }
    const inMonthRow = (rows || []).find(
      (row) =>
        row.status !== 'suspended' &&
        row.amount_frozen &&
        isSameCycleMonth(row.payout_month, selectedCycleMonth) &&
        cutoffBelongsToMonth(row.cutoff_date || row.cutoff_at, selectedCycleMonth),
    );
    if (inMonthRow) return inMonthRow.cutoff_date || inMonthRow.cutoff_at;
    return lockDate || null;
  })();
  const selectedMonthLabel = formatYearMonthLong(selectedCycleMonth, 'this month');
  const cutoffDateLabel = formatPayoutCutoffDate(finalizeCutoffDate);
  const cutoffIsToday = isoDateOnly(finalizeCutoffDate) === isoDateOnly(cycleLockInfo.today);
  const monthEndIso = lastDateOfCycleMonth(finalizeCutoffDate);
  const cutoffIsMonthEnd = Boolean(
    isoDateOnly(finalizeCutoffDate) && isoDateOnly(finalizeCutoffDate) === monthEndIso,
  );
  const monthEndLabel = formatPayoutCutoffDate(monthEndIso);
  const previousMonthEndIso = usualPreviousMonthEnd(
    cycleLockInfo.today || isoToday(),
  );
  const previousMonthEndLabel = formatPayoutCutoffDate(previousMonthEndIso);
  const selectedLockChosenBy = selectedMonthLock?.finalized_by || '';
  const selectedLockChosenAt = selectedMonthLock?.finalized_at
    ? formatAppDateTime(selectedMonthLock.finalized_at)
    : '';
  const selectedLockCutoffAt = selectedMonthLock?.cutoff_at
    ? formatAppDateTime(selectedMonthLock.cutoff_at)
    : formatPayoutCutoffDate(selectedMonthCutoff || finalizeCutoffDate);
  const firstCycleMonth = cycleMonthFromCutoff(
    cycleLockInfo.first_operational_cycle || '2026-08-01',
  );
  const selectedBeforeFirstCycle =
    Boolean(selectedCycleMonth && firstCycleMonth && selectedCycleMonth < firstCycleMonth);
  const nextMonthAfterSelected = (() => {
    if (!selectedCycleMonth) return 'the next month';
    const [y, m] = selectedCycleMonth.split('-').map(Number);
    const next = new Date(y, m, 1); // month index is m because Date month is 0-based; m is 1-12 so Date(y, m, 1) is next month
    return formatYearMonthLong(next);
  })();

  const closeFinalizeModal = () => {
    setFinalizeModalStep(0);
    setFinalizeAckChecked(false);
  };

  const handleFinalizeCycle = () => {
    if (!finalizeCutoffDate) {
      showToast('Select a cutoff date before finalizing.');
      return;
    }
    if (selectedMonthLocked) {
      showToast(
        readActionMessage({
          message: selectedMonthCutoff
            ? `${selectedMonthLabel} is already locked with cutoff ${formatPayoutCutoffDate(selectedMonthCutoff)}. That date cannot be changed.`
            : `${selectedMonthLabel} is already locked. Pick an unlocked past month if you still need to close one.`,
        }),
      );
      return;
    }
    if (selectedBeforeFirstCycle) {
      showToast(
        `Payouts start from ${formatYearMonthLong(firstCycleMonth)}. ${selectedMonthLabel} cannot be finalized.`,
      );
      return;
    }
    setFinalizeAckChecked(false);
    setFinalizeModalStep(1);
  };

  const submitLockedFinalize = async () => {
    if (!finalizeAckChecked || finalizeModalStep !== 2) return;
    setSaving(true);
    try {
      const res = await api.post(
        API.ADMIN_WITHDRAWAL_FINALIZE_CYCLE,
        {
          cutoff_date: finalizeCutoffDate,
          confirm_understood: true,
          confirm_irreversible: true,
        },
        { headers: adminHeaders() },
      );
      const data = res?.data;
      if (data && data.success === false) {
        showToast(readActionMessage(data));
        return;
      }
      if (data?.locks) {
        setCycleLockInfo({
          today: data.today || isoToday(),
          first_operational_cycle: data.first_operational_cycle || '2026-08-01',
          current_cycle_month: data.current_cycle_month || cycleMonthFromCutoff(isoToday()),
          locked_cycle_months: data.locked_cycle_months || [],
          locks: data.locks || [],
        });
      }
      showToast(
        data?.message
          || `${selectedMonthLabel} locked · cutoff ${data?.cutoff_date || finalizeCutoffDate}.`,
      );
      closeFinalizeModal();
      await load();
    } catch (e) {
      showToast(readActionMessage(e.response?.data, 'Could not finalize payout cycle.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setSaving(true);
    try {
      const res = await api.post(
        API.ADMIN_WITHDRAWAL_RECALCULATE(detail.id),
        {},
        { headers: adminHeaders() }
      );
      setDetail(res.data?.withdrawal || detail);
      showToast('Latest earnings recalculated for this month.');
      await load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Recalculate failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateUser = async (userId) => {
    if (!userId) return;
    setSaving(true);
    try {
      const res = await api.post(
        API.ADMIN_WITHDRAWAL_USER_RECALCULATE(userId),
        {},
        { headers: adminHeaders() }
      );
      showToast(res.data?.message || 'Member payout cycles recalculated.');
      if (detail?.user_id === userId) {
        const match = (res.data?.withdrawals || []).find((w) => w.id === detail.id);
        if (match) setDetail(match);
      }
      await load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Member recalculate failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!proofFile) {
      showToast('Payment proof is required to record a payment.');
      return;
    }
    const remaining = Number(detail.remaining_amount ?? detail.net_payable_amount ?? 0);
    if (remaining <= 0 && (detail.payment_status || '') === 'paid') {
      showToast('Nothing remaining to pay on this payout.');
      return;
    }
    if (payAmount === '' || payAmount == null) {
      showToast(`Enter the amount you transferred. Remaining to pay is ₹${formatInr(remaining)}.`);
      return;
    }
    const entered = Number(payAmount);
    if (!Number.isFinite(entered) || entered <= 0) {
      showToast('Enter a payment amount greater than zero.');
      return;
    }
    if (entered > remaining + 0.001) {
      const alreadyPaid = Number(detail.paid_amount ?? 0);
      showToast(
        alreadyPaid > 0
          ? `Already paid ₹${formatInr(alreadyPaid)}. Remaining to pay is ₹${formatInr(remaining)}, not ₹${formatInr(entered)}.`
          : `₹${formatInr(entered)} exceeds remaining to pay ₹${formatInr(remaining)}.`,
      );
      return;
    }
    const neededPts = Number(detail.amount ?? detail.gross_amount ?? 0);
    if (neededPts <= 0) {
      showToast(detail.mark_paid_block_reason || 'Payable amount must be greater than zero.');
      return;
    }
    if (!detail.bank_details_ok) {
      showToast(
        detail.bank_block_reason
          || 'Member must update bank / UPI details before payment. Ask them via mobile or email first.'
      );
      return;
    }
    const form = new FormData();
    form.append('payment_proof', proofFile);
    if (adminNote) form.append('admin_note', adminNote);
    if (paymentReference.trim()) form.append('payment_reference', paymentReference.trim());
    form.append('amount', String(payAmount));
    setSaving(true);
    try {
      const res = await api.post(API.ADMIN_WITHDRAWAL_MARK_PAID(detail.id), form, {
        headers: adminHeaders(),
      });
      const data = res?.data;
      if (data && data.success === false) {
        showToast(readActionMessage(data));
        if (data.withdrawal) setDetail(data.withdrawal);
        return;
      }
      showToast(data?.message || 'Payment recorded.');
      setProofFile(null);
      setPayAmount('');
      setPaymentReference('');
      await refreshMemberPayoutView(data?.withdrawal || detail);
    } catch (e) {
      showToast(readActionMessage(e.response?.data, 'Please check the details and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReviewConcern = () =>
    runAction(
      () =>
        api.post(
          API.ADMIN_WITHDRAWAL_REVIEW_CONCERN(detail.id),
          { admin_note: adminNote },
          { headers: adminHeaders() }
        ),
      'Concern under review'
    );

  const handleResolveConcern = () => {
    if (!showAdminNotes) {
      setShowAdminNotes(true);
      showToast('Enter resolution note in Notes, then tap Resolve concern again.');
      return;
    }
    if ((adminNote || '').trim().length < 5) {
      showToast('Resolution note must be at least 5 characters.');
      return;
    }
    return runAction(
      () =>
        api.post(
          API.ADMIN_WITHDRAWAL_RESOLVE_CONCERN(detail.id),
          { admin_note: adminNote },
          { headers: adminHeaders() }
        ),
      'Concern resolved'
    );
  };

  const handleBankEditToggle = async (allow) => {
    setSaving(true);
    try {
      const res = await api.post(
        API.ADMIN_BANK_DETAILS_EDIT(detail.user_id),
        {
          allow,
          admin_note:
            adminNote
            || (allow
              ? 'Admin unlocked one-time bank/UPI edit during payout.'
              : 'Admin re-locked bank/UPI edits.'),
        },
        { headers: adminHeaders() }
      );
      const data = res?.data;
      if (data && data.success === false) {
        showToast(readActionMessage(data));
        return;
      }
      showToast(
        data?.message
          || (allow
            ? 'Member can update bank / UPI once.'
            : 'Bank / UPI edits locked again.')
      );
      await openDetail(detail);
    } catch (e) {
      showToast(readActionMessage(e.response?.data, 'Please check and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = (action) => {
    if (!showAdminNotes) {
      setDetailTab('admin');
      setShowAdminNotes(true);
      showToast('Enter revert reason in Notes (min 10 characters), then tap Revert again.');
      return;
    }
    if ((adminNote || '').trim().length < 10) {
      showToast('Revert reason required in Notes (min 10 characters) — saved to audit log.');
      return;
    }
    const labels = {
      approve: 'Approve reverted — back on Not Started',
      mark_paid: 'Mark Paid reverted — back on Yet To Be Paid',
      completed: 'Done reverted — points restored to wallet',
    };
    return runAction(
      () =>
        api.post(
          API.ADMIN_WITHDRAWAL_REVERT(detail.id),
          { action, admin_note: adminNote },
          { headers: adminHeaders() }
        ),
      labels[action] || 'Reverted'
    );
  };

  return (
    <div className="space-y-4">
      {toast ? (
        <div
          role="alertdialog"
          aria-live="assertive"
          className="fixed top-4 left-1/2 z-[80] flex w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-ds-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm font-semibold text-brand-800 shadow-lg"
        >
          <p className="min-w-0 flex-1 whitespace-pre-wrap">{toast}</p>
          <button
            type="button"
            className="flex-shrink-0 rounded-ds-lg border border-brand-200 bg-white px-2 py-1 text-xs font-bold text-brand-800 hover:bg-brand-100"
            onClick={() => setToast('')}
          >
            Close
          </button>
        </div>
      ) : null}

      {showingMemberPayout ? null : (
      <>
      <div>
        <h2 className="text-xl font-black text-ink">People waiting to be paid</h2>
        <p className="text-sm text-ink-muted mt-1">
          One payout per member per cycle. Filter by Yet to be Paid / Partially Paid / Paid / Payment Issues / Failed.
          Record partial or full payments with proof — wallet debit happens on each successful payment.
          Member acknowledgement is optional and does not change payment status.
        </p>
      </div>

      <div className="rounded-ds-xl border border-line bg-surface-muted px-4 py-3 text-sm text-ink space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-ink-muted">How to use</p>
        <ol className="list-decimal pl-5 space-y-1 text-xs text-ink leading-snug">
          <li>
            Choose a <strong>Cutoff date</strong>, then tap <strong>Finalize cycle</strong> to create this month&apos;s
            payouts (only once when you are ready).
          </li>
          <li>Set filters, tap <strong>Apply Filters</strong>, then open a member payout.</li>
          <li>Confirm bank / UPI is ready before paying.</li>
          <li>
            After you transfer money, type the exact amount, upload proof, and tap{' '}
            <strong>Record payment</strong>. You can pay in parts.
          </li>
          <li>
            Payment Issues shows open concern tickets. Review / resolve there — payment status stays independent.
          </li>
        </ol>
        <button
          type="button"
          className="text-xs font-bold text-brand-700 hover:underline"
          onClick={() => setShowMoreHelp((v) => !v)}
        >
          {showMoreHelp ? 'Hide details' : 'Know more'}
        </button>
        {showMoreHelp ? (
          <div className="text-xs text-ink-muted space-y-2 pt-2 border-t border-line leading-relaxed">
            <p className="font-semibold text-ink">Simple flow</p>
            <ol className="list-decimal pl-5 space-y-1.5">
              <li>
                <strong>Cutoff date</strong> — pick today or a past date (never a future date).
                That day belongs to that month, and that month can be locked only once.
                People usually pick month-end (28/29/30/31), for example 31-Aug.
                If you pick a date that is not month-end, the popup will ask you to confirm.
              </li>
              <li>
                <strong>Finalize cycle</strong> — tick the box and confirm twice.
                After that, Finalize stays off for that month. You cannot pick another day
                in the same month. Next month (or another unlocked month) can still be finalized once.
              </li>
              <li>
                <strong>Record payment</strong> — after you send money to the member, upload proof and save the payment
                (full or partial). Points leave the wallet when payment is recorded.
              </li>
              <li>
                <strong>Member confirm / concern</strong> — optional. Acknowledgement does not change Paid status.
                If something is wrong, the member can raise a concern ticket.
              </li>
            </ol>
            <p>
              If earlier months were not paid, Finalize on the current month folds those unpaid
              months into this cycle: one Gross, 5% TDS once on the combined total, one Net to pay.
              Old month rows stay as history (Suspended) so the same earnings are never paid twice.
              Months that already have a successful payment are not moved.
            </p>
            <p>
              Use filters to find Yet to be Paid / Partially Paid / Paid / Payment Issues / Failed.
              Bank or UPI must be on the member profile before you can pay.
            </p>
          </div>
        ) : null}
      </div>

      <ListToolbar
        search={draftFilters.search}
        onSearchChange={(value) => setDraftFilters((prev) => ({ ...prev, search: value }))}
        searchPlaceholder="Search name, phone, email…"
        onApply={applyFilters}
        onClear={clearFilters}
        footer={
          <div className="flex w-full flex-col gap-3">
            <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                Cutoff date
                <button
                  type="button"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
                  aria-label="Cutoff date help"
                  title="How cutoff date works"
                  onClick={() => setShowCutoffHelp(true)}
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                </button>
              </span>
              <input
                type="date"
                className={ds.input}
                value={finalizeCutoffDate}
                min={cycleLockInfo.first_operational_cycle || '2026-08-01'}
                max={cycleLockInfo.today || isoToday()}
                onChange={(e) => setFinalizeCutoffDate(e.target.value)}
                aria-label="Earnings cutoff date"
                title="Usual business date is month-end (28/29/30/31). That month can be locked only once."
              />
            </div>
            <AppButton
              type="button"
              variant="success"
              icon="check"
              disabled={
                saving ||
                loading ||
                !finalizeCutoffDate ||
                selectedMonthLocked ||
                selectedBeforeFirstCycle
              }
              onClick={handleFinalizeCycle}
              title={
                selectedMonthLocked
                  ? `${selectedMonthLabel} already has a cutoff. Pick a date in another month.`
                  : 'Lock this month. You cannot undo.'
              }
            >
              Finalize cycle
            </AppButton>
            <AppButton type="button" variant="refresh" icon="refresh" onClick={() => load()}>
              Refresh list
            </AppButton>
            </div>
          </div>
        }
      >
        <ToolbarField label="Payout status">
          <select
            className={ds.select}
            value={draftFilters.status}
            onChange={(e) => setDraftFilters((prev) => ({ ...prev, status: e.target.value }))}
            aria-label="Filter by member payout status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </ToolbarField>
        <ToolbarField label="Bank / UPI">
          <select
            className={ds.select}
            value={draftFilters.bankFilter}
            onChange={(e) => setDraftFilters((prev) => ({ ...prev, bankFilter: e.target.value }))}
          >
            <option value="">All</option>
            <option value="updated">Has bank / UPI</option>
            <option value="missing">Missing bank / UPI</option>
          </select>
        </ToolbarField>
        <ToolbarField label="Created (quick)">
          <select
            className={ds.select}
            value={draftFilters.datePreset}
            onChange={(e) => {
              const value = e.target.value;
              setDraftFilters((prev) => ({
                ...prev,
                datePreset: value,
                ...(value ? { dateFrom: '', dateTo: '' } : {}),
              }));
            }}
          >
            <option value="">Any time</option>
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
        </ToolbarField>
        <ToolbarField label="From date">
          <input
            type="date"
            className={ds.input}
            value={draftFilters.dateFrom}
            onChange={(e) => setDraftFilters((prev) => ({
              ...prev,
              dateFrom: e.target.value,
              datePreset: '',
            }))}
          />
        </ToolbarField>
        <ToolbarField label="To date">
          <input
            type="date"
            className={ds.input}
            value={draftFilters.dateTo}
            onChange={(e) => setDraftFilters((prev) => ({
              ...prev,
              dateTo: e.target.value,
              datePreset: '',
            }))}
          />
        </ToolbarField>
      </ListToolbar>

      {error ? (
        <div className="rounded-ds-xl border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-800">{error}</div>
      ) : null}

      <DataTable
        columns={COLUMNS}
        loading={loading}
        empty={!loading && visibleMemberGroups.length === 0}
        emptyTitle="No one in this view"
        emptyHint={emptyListHint}
        colSpan={COLUMNS.length}
        minWidth="60rem"
        page={pagedMemberGroups.page}
        totalPages={pagedMemberGroups.totalPages}
        pageSize={pageSize}
        count={pagedMemberGroups.count}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        rowsPerPageId="admin-withdrawals-rows"
        ariaLabel="People waiting to be paid"
      >
        {pagedMemberGroups.items.map((g) => {
          const displayName = String(g.member_name || g.username || '').trim();
          const handle = String(g.username || '').trim();
          const showHandle =
            Boolean(handle) &&
            Boolean(g.member_name) &&
            displayName.toLowerCase() !== handle.toLowerCase();
          return (
            <tr key={g.user_id} className={ds.row}>
              <td className={ds.td}>
                <button
                  type="button"
                  className="font-semibold text-brand-700 hover:text-brand-900 hover:underline text-left"
                  title="Open member profile"
                  onClick={() => openMemberProfile(g.user_id)}
                >
                  {displayName || '—'}
                </button>
                <p className="text-[11px] text-ink-muted">
                  ID {g.user_id}
                  {showHandle ? ` · @${handle}` : ''}
                  {g.phone ? ` · ${g.phone}` : ''}
                </p>
              </td>
            
              <td className={ds.td}>
                <p className="font-semibold tabular-nums text-ink">
                  ₹{formatInr(g.total_payable)}
                </p>
              </td>
              <td className={ds.td}>
                <p className="font-semibold tabular-nums text-ink">
                  ₹{formatInr(g.total_paid_amount)}
                </p>
              </td>
              <td className={ds.td}>
                <p className="font-semibold tabular-nums text-amber-800">
                  ₹{formatInr(g.total_remaining)}
                </p>
              </td>
              <td className={ds.td}>
                <StatusBadge tone={g.status_tone || 'neutral'} uppercase={false}>
                  {g.status_label}
                </StatusBadge>
              </td>
              <td className={ds.td}>
                <span
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${
                    g.bank_details_ok
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                  title={
                    g.bank_details_ok
                      ? 'Bank / UPI details ready'
                      : 'Bank / UPI details missing'
                  }
                  aria-label={
                    g.bank_details_ok
                      ? 'Bank / UPI details ready'
                      : 'Bank / UPI details missing'
                  }
                >
                  {g.bank_details_ok ? (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path d="M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
              </td>
              <td className={ds.td}>
                <AppButton
                  type="button"
                  variant="primary"
                  icon="eye"
                  size="sm"
                  className="!px-2.5"
                  title="View payout details"
                  aria-label="View payout details"
                  onClick={() => openMemberGroup(g)}
                />
              </td>
            </tr>
          );
        })}
      </DataTable>
      </>
      )}

      {showingMemberPayout ? (
        <div className="space-y-4">
          <button
            type="button"
            onClick={closeMemberPayoutView}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900 transition-colors"
          >
            ← Back to withdrawals list
          </button>

          <div className="w-full max-w-3xl rounded-ds-2xl border border-line bg-surface shadow-ds-card">
            <div className="border-b border-line px-5 py-4">
              <h3 className="text-lg font-black text-ink">
                {detail?.member_name || detail?.username || 'Member payouts'}
              </h3>
              <p className="text-xs text-ink-muted mt-0.5">
                {detail
                  ? (
                    <>
                      Member payouts · viewing {payoutMonthLabel(detail.payout_month)} #{detail.cycle_no || 1}
                      {(detail.phone || detail.mobile || '').trim()
                        ? ` · ${(detail.phone || detail.mobile || '').trim()}`
                        : ''}
                    </>
                  )
                  : 'Loading member payouts…'}
              </p>
            </div>

            {detailLoading || !detail ? (
              <p className="p-6 text-sm text-ink-muted">Loading…</p>
            ) : (
              <div className="space-y-4 p-5">
                <div
                  role="tablist"
                  aria-label="Member payout sections"
                  className="flex flex-wrap gap-1 border-b border-line"
                >
                  {[
                    { id: 'months', label: `Payouts (${memberHistory.filter((h) => h.status !== 'suspended').length})` },
                    { id: 'details', label: 'Details' },
                    { id: 'timeline', label: 'Payments & timeline' },
                    { id: 'admin', label: 'Admin' },
                  ].map((tab) => {
                    const active = detailTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        id={`wd-tab-${tab.id}`}
                        className={`relative -mb-px px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                          active
                            ? 'text-brand-700'
                            : 'text-ink-muted hover:text-ink'
                        }`}
                        onClick={() => setDetailTab(tab.id)}
                      >
                        {tab.label}
                        <span
                          className={`absolute inset-x-2 bottom-0 h-0.5 rounded-full transition-all ${
                            active ? 'bg-brand-600' : 'bg-transparent'
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>

                {detailTab === 'months' ? (
                  <div className="space-y-3">
                    <p className="text-[10px] font-bold uppercase text-ink-muted">
                      What to pay this member
                    </p>
                    <div className="rounded-ds-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                      <p className="text-sm text-ink">
                        Wallet on the member profile is <strong>all points ever earned</strong>.
                        It is not the bank transfer. Old grey months below are cancelled drafts — do not add them.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="rounded-ds-lg border border-line bg-white p-2">
                          <p className="text-[11px] font-bold uppercase text-ink-muted">1. Wallet now</p>
                          <p className="text-lg font-bold tabular-nums text-ink">
                            {Number(detail.available_points ?? detail.wallet_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                          <p className="text-[11px] text-ink-muted">All points on the member. Not the bank transfer.</p>
                        </div>
                        <div className="rounded-ds-lg border-2 border-emerald-400 bg-emerald-50 p-2">
                          <p className="text-[11px] font-bold uppercase text-emerald-800">2. Pay this time</p>
                          <p className="text-lg font-bold tabular-nums text-emerald-900">
                            {Number(detail.unsettled_gross_points ?? detail.earned_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                          <p className="text-[11px] text-emerald-800">Only these points. Tax 5% is cut on the green ₹ below.</p>
                        </div>
                        <div className="rounded-ds-lg border-2 border-amber-400 bg-amber-50 p-2">
                          <p className="text-[11px] font-bold uppercase text-amber-800">3. Leftover</p>
                          <p className="text-lg font-bold tabular-nums text-amber-900">
                            {Number(detail.carry_forward_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                          <p className="text-[11px] text-amber-800">Points earned after this cutoff. Paid next time that month is closed.</p>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-ink tabular-nums">
                        {Number(detail.available_points ?? detail.wallet_points ?? 0).toLocaleString('en-IN')}
                        {' wallet − '}
                        {Number(detail.unsettled_gross_points ?? detail.earned_points ?? 0).toLocaleString('en-IN')}
                        {' this payout = '}
                        {Number(detail.carry_forward_points ?? 0).toLocaleString('en-IN')} pts
                        {' leftover (points after this cutoff).'}
                      </p>
                    </div>
                    {payTogetherSummary && payTogetherSummary.unpaid_count > 0 ? (
                      <div className="rounded-ds-xl border-2 border-emerald-300 bg-emerald-50/50 p-4 space-y-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">
                            {payTogetherSummary.unpaid_count > 1
                              ? 'Send this much now (all unpaid cycles, after 5% tax)'
                              : 'Send this much now (after 5% tax)'}
                          </p>
                          <p className="mt-0.5 text-2xl font-semibold tabular-nums text-emerald-900">
                            ₹{formatInr(payTogetherSummary.total_payable)}
                          </p>
                          <p className="text-xs text-ink-muted mt-1">
                            From {Number(payTogetherSummary.total_gross).toLocaleString('en-IN')} pts
                            {' · '}tax ₹{formatInr(payTogetherSummary.total_tds)}
                            {payTogetherSummary.unpaid_count > 1 ? ' (once on total)' : ''}
                            {Number(detail.wallet_points ?? detail.available_points ?? 0) > 0
                              ? ` · wallet still ${Number(detail.wallet_points ?? detail.available_points).toLocaleString('en-IN')} pts until you record payment`
                              : ''}
                          </p>
                        </div>
                        {payTogetherSummary.unpaid_count > 1 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <AppButton
                              type="button"
                              variant="success"
                              icon="pay"
                              size="md"
                              className="shadow-sm ring-1 ring-emerald-800/15 transition hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                              onClick={() => {
                                const first =
                                  payTogetherSummary.unpaid_months?.[0] ||
                                  memberHistory.find((h) => isWithdrawalUnpaidStatus(h.status, h));
                                if (first) {
                                  openDetail(first, { keepPayTogether: true });
                                  setDetailTab('details');
                                }
                              }}
                            >
                              Pay all unpaid months
                            </AppButton>
                            <p className="text-xs text-emerald-900">
                              Transfer ₹{formatInr(payTogetherSummary.total_payable)} once, then Mark Paid on each unpaid month.
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-emerald-900">
                            Transfer only this green amount. Open the live payout below (not the grey old months).
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-ink-muted">
                        No unpaid months — paid and finished cycles are listed below.
                        {memberHistory.filter((h) => isWithdrawalUnpaidStatus(h.status, h)).length <= 1
                          ? ' Pay together appears only when this member has 2+ unpaid months.'
                          : ''}
                      </p>
                    )}
                    {memberHistory.length === 0 ? (
                      <p className="text-sm text-ink-muted py-4">No month records yet.</p>
                    ) : (
                      <>
                        <AdminEarningsByMonth detail={detail} />
                        <p className="text-[10px] font-bold uppercase text-ink-muted pt-1">
                          Live payouts
                        </p>
                        <ul className="space-y-2">
                          {memberHistory
                            .filter((h) => h.status !== 'suspended')
                            .map((h) => (
                              <MemberMonthRow
                                key={h.id}
                                h={h}
                                detail={detail}
                                memberHistory={memberHistory}
                                openDetail={openDetail}
                                setDetailTab={setDetailTab}
                                setMedia={setMedia}
                              />
                            ))}
                        </ul>
                        {memberHistory.some((h) => h.status === 'suspended') ? (
                          <details className="rounded-ds-xl border border-line bg-slate-50 p-3">
                            <summary className="cursor-pointer text-sm font-semibold text-ink">
                              Old month drafts (
                              {memberHistory.filter((h) => h.status === 'suspended').length}
                              ) — not extra money
                            </summary>
                            <p className="text-xs text-ink-muted mt-2 mb-3">
                              These drafts were cancelled so the same points are not paid twice.
                              Ignore the old point totals. Pay only the green amount above.
                            </p>
                            <ul className="space-y-2">
                              {memberHistory
                                .filter((h) => h.status === 'suspended')
                                .map((h) => (
                                  <MemberMonthRow
                                    key={h.id}
                                    h={h}
                                    detail={detail}
                                    memberHistory={memberHistory}
                                    openDetail={openDetail}
                                    setDetailTab={setDetailTab}
                                    setMedia={setMedia}
                                  />
                                ))}
                            </ul>
                          </details>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : detailTab === 'timeline' ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-xs text-ink-muted">
                      <p>Created: {formatWhen(detail.created_at)}</p>
                      <p>Updated: {formatWhen(detail.updated_at)}</p>
                      <p>Approved: {formatWhen(detail.approved_at)}</p>
                      <p>Paid: {formatWhen(detail.paid_at)}</p>
                      <p>Acknowledged: {formatWhen(detail.acknowledged_at)}</p>
                      <p>Processing: {formatDuration(detail.processing_seconds)}</p>
                    </div>
                    {allMemberPaymentTxns(detail, memberHistory).length > 0 ? (
                      <div className="rounded-ds-xl border border-line p-3 space-y-2">
                        <p className="text-[10px] font-bold uppercase text-ink-muted">
                          All payment attempts for this member (success and failed)
                        </p>
                        <ul className="space-y-2">
                          {allMemberPaymentTxns(detail, memberHistory).map((t) => (
                            <li
                              key={t.id}
                              className="rounded-ds-lg border border-line bg-surface-muted/40 px-3 py-2 text-xs"
                            >
                              <p className="font-semibold text-ink">
                                {payoutMonthLabel(t.payout_month)}
                                {' · '}₹{formatInr(t.amount)}
                                {' · '}
                                Points paid: {Number(t.amount || 0).toLocaleString('en-IN')} pts
                                {' · '}
                                Wallet debited: {Number(t.points_debited || 0).toLocaleString('en-IN')} pts
                                {' · '}
                                {t.status}
                              </p>
                              <p className="text-ink-muted mt-0.5">
                                {t.paid_at ? formatWhen(t.paid_at) : formatWhen(t.created_at)}
                                {t.processed_by ? ` · ${t.processed_by}` : ''}
                                {t.payment_reference ? ` · ref ${t.payment_reference}` : ''}
                                {t.cycle_status === 'suspended' ? ' · from a rolled-in month' : ''}
                              </p>
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-ink-muted">
                          This cycle paid ₹{formatInr(detail.paid_amount)}
                          {' · '}Remaining ₹{formatInr(detail.remaining_amount)}
                          {' · '}Wallet points debited on this payout:{' '}
                          {Number(detail.points_debited || 0).toLocaleString('en-IN')} pts
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-ink-muted">
                        No payment attempts yet for this member (success or failed).
                      </p>
                    )}
                    <div>
                      <p className="text-[10px] font-bold uppercase text-ink-muted mb-2">
                        Event log
                      </p>
                      <ul className="space-y-2 border-l-2 border-line pl-3">
                        {(detail.timeline || []).map((ev) => (
                          <li key={ev.id} className="text-xs">
                            <p className="font-semibold text-ink">{ev.event_name}</p>
                            <p className="text-ink-muted">
                              {formatWhen(ev.created_at)} · {ev.actor}
                              {ev.previous_status || ev.new_status
                                ? ` · ${ev.previous_status || '—'} → ${ev.new_status || '—'}`
                                : ''}
                            </p>
                            {ev.notes ? (
                              <p className="text-ink-muted mt-0.5">
                                {formatYearMonthTokensInText(ev.notes)}
                              </p>
                            ) : null}
                          </li>
                        ))}
                        {(detail.timeline || []).length === 0 ? (
                          <li className="text-xs text-ink-muted">No events yet.</li>
                        ) : null}
                      </ul>
                    </div>
                  </div>
                ) : detailTab === 'admin' ? (
                  <div className="space-y-4">
                    {detail.can_unlock_bank_edit ? (
                      <div className="rounded-ds-xl border border-line bg-surface-muted p-4 space-y-3">
                        <p className="text-[10px] font-bold uppercase text-ink-muted">
                          Bank / UPI lock
                        </p>
                        <p className="text-xs text-ink-muted">
                          After Approve, the member cannot change payout details (avoids sending money to a new account).
                          Unlock only if you verified their request — they get one save, then it locks again and this cycle&apos;s snapshot updates.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {detail.bank_lock?.bank_details_edit_allowed ? (
                            <AppButton
                              type="button"
                              variant="secondary"
                              icon="lock"
                              disabled={saving}
                              onClick={() => handleBankEditToggle(false)}
                            >
                              Re-lock bank edit
                            </AppButton>
                          ) : (
                            <AppButton
                              type="button"
                              variant="warning"
                              icon="unlock"
                              disabled={saving}
                              onClick={() => handleBankEditToggle(true)}
                            >
                              Unlock bank edit (one-time)
                            </AppButton>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-ink-muted">
                        Bank / UPI lock controls appear after this month is approved for payout.
                      </p>
                    )}

                    {(detail.can_revert_approve
                      || detail.can_revert_mark_paid
                      || detail.can_revert_completed) ? (
                      <div className="rounded-ds-xl border border-danger-100 bg-danger-50/40 p-4 space-y-3">
                        <p className="text-[10px] font-bold uppercase text-danger-800">
                          Emergency revert (logged to database)
                        </p>
                        <p className="text-xs text-ink-muted">
                          Tap a revert button to open Notes (min 10 chars). Every revert writes WithdrawalEvent + FinancialAuditLog.
                        </p>
                        {showAdminNotes ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <label className={ds.label}>Notes</label>
                              <AppButton
                                type="button"
                                variant="secondary"
                                size="sm"
                                icon="x"
                                onClick={() => {
                                  setShowAdminNotes(false);
                                  setAdminNote('');
                                }}
                              >
                                Hide notes
                              </AppButton>
                            </div>
                            <textarea
                              className={ds.input}
                              rows={3}
                              value={adminNote}
                              onChange={(e) => setAdminNote(e.target.value)}
                              placeholder="Revert reason (min 10 characters)…"
                              autoFocus
                            />
                          </div>
                        ) : null}
                        <div className="flex flex-wrap gap-2">
                          {detail.can_revert_approve ? (
                            <AppButton
                              type="button"
                              variant="danger"
                              icon="undo"
                              disabled={saving}
                              onClick={() => handleRevert('approve')}
                            >
                              Revert Approve
                            </AppButton>
                          ) : null}
                          {detail.can_revert_mark_paid ? (
                            <AppButton
                              type="button"
                              variant="danger"
                              icon="undo"
                              disabled={saving}
                              onClick={() => handleRevert('mark_paid')}
                            >
                              Revert Mark Paid
                            </AppButton>
                          ) : null}
                          {detail.can_revert_completed ? (
                            <AppButton
                              type="button"
                              variant="danger"
                              icon="undo"
                              disabled={saving}
                              onClick={() => handleRevert('completed')}
                            >
                              Revert Done (restore points)
                            </AppButton>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-ink-muted">
                        No emergency revert actions available for this month&apos;s status.
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    tone={
                      detail.payment_status === 'partially_paid'
                        ? 'warning'
                        : (detail.status_tone || 'neutral')
                    }
                    uppercase={false}
                  >
                    {detail.payment_status === 'partially_paid'
                      ? (detail.payment_status_label || 'Partially Paid')
                      : (detail.status_label || detail.status)}
                  </StatusBadge>
                </div>

                <div className="rounded-ds-xl border border-slate-200 bg-slate-50 px-3 py-3 space-y-3">
                  <p className="text-sm font-bold text-ink">Simple pay guide</p>
                  {detail.status === 'suspended' ? (
                    <p className="text-sm text-ink">
                      ❌ Do not pay this one. It was moved to a later month so we do not pay twice.
                    </p>
                  ) : (
                    <>
                      <ul className="text-sm text-ink space-y-1">
                        <li>
                          📅 Month:{' '}
                          <strong>{formatYearMonthLong(detail.payout_month)}</strong>
                        </li>
                        <li>
                          📅 We count points till:{' '}
                          <strong>
                            {detail.cutoff_at ? formatPayoutCutoffDate(detail.cutoff_at) : '—'}
                          </strong>
                        </li>
                        {detail.finalized_at ? (
                          <li>
                            🔒 Amount fixed on:{' '}
                            <strong>{formatAppDateTime(detail.finalized_at)}</strong>
                          </li>
                        ) : null}
                      </ul>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="rounded-ds-lg border border-line bg-white p-2">
                          <p className="text-[11px] text-ink-muted">Member has now</p>
                          <p className="text-lg font-bold tabular-nums text-ink">
                            {Number(detail.available_points ?? detail.wallet_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                        </div>
                        <div className="rounded-ds-lg border border-emerald-200 bg-emerald-50 p-2">
                          <p className="text-[11px] text-emerald-800">Pay this time</p>
                          <p className="text-lg font-bold tabular-nums text-emerald-900">
                            {Number(detail.unsettled_gross_points ?? detail.earned_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                        </div>
                        <div className="rounded-ds-lg border border-amber-200 bg-amber-50 p-2">
                          <p className="text-[11px] text-amber-800">Keep for next month</p>
                          <p className="text-lg font-bold tabular-nums text-amber-900">
                            {Number(detail.carry_forward_points ?? 0).toLocaleString('en-IN')} pts
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-ink tabular-nums">
                        {Number(detail.available_points ?? detail.wallet_points ?? 0).toLocaleString('en-IN')}
                        {' wallet − '}
                        {Number(detail.unsettled_gross_points ?? detail.earned_points ?? 0).toLocaleString('en-IN')}
                        {' this payout = '}
                        {Number(detail.carry_forward_points ?? 0).toLocaleString('en-IN')} pts leftover.
                      </p>
                      <AdminEarningsByMonth detail={detail} />
                      {Number(detail.carry_forward_points ?? 0) > 0 ? (
                        <p className="text-sm text-ink">
                          👉 Send money only for <strong>Pay this time</strong> (see green ₹ below, tax already cut).
                          {' '}
                          <strong>Keep for next month</strong> is extra points after cutoff{' '}
                          <strong>{lockedCutoffDateLabel(detail) || 'the date above'}</strong>. Do not pay that now.
                        </p>
                      ) : (
                        <p className="text-sm text-ink">
                          👉 All points are in this pay. Nothing is waiting for next month.
                          Send the green ₹ amount below (tax 5% is already cut).
                        </p>
                      )}
                      <p className="text-sm text-ink-muted">
                        After you save the payment, the member’s points go down.
                        The “total earned” number on profile does not go down.
                      </p>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded-ds-xl border border-line p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {(detail.payout_breakdown?.months || []).some(
                        (m) => m.source === 'rolled_in' || (m.earning_month && m.earning_month !== detail.payout_month),
                      )
                        ? 'Payout gross'
                        : payTogetherSummary
                          ? 'This month gross'
                          : 'Gross amount'}
                    </p>
                    <p className="mt-0.5 text-xl font-semibold tabular-nums text-ink">
                      ₹{Number(detail.gross_amount ?? detail.amount ?? 0).toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-ink-muted mt-1">
                      ⭐ {Number(detail.earned_points || 0).toLocaleString('en-IN')} pts
                    </p>
                  </div>
                  <div className="rounded-ds-xl border border-line p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {(detail.payout_breakdown?.months || []).some(
                        (m) => m.source === 'rolled_in' || (m.earning_month && m.earning_month !== detail.payout_month),
                      )
                        ? `Payout TDS (${Number(detail.tds_percent ?? 5)}%)`
                        : payTogetherSummary
                        ? 'This month TDS'
                        : `TDS (${Number(detail.tds_percent ?? 5)}%)`}
                    </p>
                    <p className="mt-0.5 text-xl font-semibold tabular-nums text-amber-800">
                      ₹{formatInr(detail.tds_amount ?? 0)}
                    </p>
                  </div>
                  <div
                    className={`rounded-ds-xl p-3 ${
                      Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                        ? 'border-2 border-emerald-300 bg-emerald-50/50'
                        : payTogetherSummary
                        ? 'border border-line'
                        : 'border-2 border-emerald-300 bg-emerald-50/50'
                    }`}
                  >
                    <p
                      className={`text-xs font-medium uppercase tracking-wide ${
                        Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                          ? 'text-emerald-800'
                          : payTogetherSummary
                            ? 'text-ink-muted'
                            : 'text-emerald-800'
                      }`}
                    >
                      {Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                        ? 'Remaining to pay'
                        : (detail.payout_breakdown?.months || []).some(
                            (m) => m.source === 'rolled_in' || (m.earning_month && m.earning_month !== detail.payout_month),
                          )
                        ? 'Net after TDS'
                        : payTogetherSummary
                        ? 'This month net'
                        : 'Net amount payable'}
                    </p>
                    <p
                      className={`mt-0.5 text-xl font-semibold tabular-nums ${
                        Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                          ? 'text-emerald-900'
                          : payTogetherSummary
                            ? 'text-ink'
                            : 'text-emerald-900'
                      }`}
                    >
                      ₹{formatInr(
                        Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                          ? (detail.remaining_amount ?? 0)
                          : (detail.net_payable_amount ?? detail.withdrawal_amount_inr ?? 0),
                      )}
                    </p>
                    <p
                      className={`text-xs mt-1 ${
                        Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                          ? 'text-emerald-800'
                          : payTogetherSummary
                            ? 'text-ink-muted'
                            : 'text-emerald-800'
                      }`}
                    >
                      {detail.status === 'suspended'
                        ? `${rolledIntoLabel(detail, memberHistory)} — pay that cycle`
                        : Number(detail.paid_amount ?? 0) > 0 && Number(detail.remaining_amount ?? 0) > 0
                        ? `Net after TDS ₹${formatInr(detail.net_payable_amount)} · already paid ₹${formatInr(detail.paid_amount)}`
                        : payTogetherSummary
                        ? 'Part of cycle payout — pay Remaining to Pay below'
                        : 'Pay this amount'}
                    </p>
                  </div>
                </div>

                {(() => {
                  const includedMonths = includedMonthsForCycle(detail, memberHistory);
                  const formula = detail.payout_breakdown?.formula;
                  if (includedMonths.length === 0 && !formula) return null;
                  const rolled = includedMonths.some(
                    (a) => a.source === 'rolled_in' || (a.earning_month && a.earning_month !== detail.payout_month),
                  );
                  return (
                  <div className="rounded-ds-xl border border-line p-3 text-sm space-y-2">
                    <p className="text-[10px] font-bold uppercase text-ink-muted">
                      {detail.status === 'suspended'
                        ? 'This month (rolled into a later cycle)'
                        : rolled
                          ? 'How this payout is calculated'
                          : 'Earning months in this payout'}
                    </p>
                    {formula && detail.status !== 'suspended' ? (
                      <p className="text-sm text-ink leading-relaxed">{formula}</p>
                    ) : null}
                    {includedMonths.length > 0 ? (
                    <ul className="space-y-1">
                      {includedMonths.map((a) => {
                        const lineTds = computeTdsBreakdown(a.gross_points || 0);
                        const rolledIn =
                          a.source === 'rolled_in' ||
                          (a.earning_month && a.earning_month !== detail.payout_month);
                        return (
                        <li
                          key={`${detail.id}-${a.earning_month}`}
                          className="flex items-center justify-between gap-2 text-sm text-ink"
                        >
                          <span>
                            {payoutMonthLabel(a.earning_month)}
                            {rolledIn ? (
                              <span className="ml-1 text-[11px] font-medium text-ink-muted">
                                (unpaid month included here)
                              </span>
                            ) : (
                              <span className="ml-1 text-[11px] font-medium text-ink-muted">
                                (this cycle)
                              </span>
                            )}
                          </span>
                          <span className="font-semibold tabular-nums text-right">
                            Earned ₹{formatInr(a.gross_points || 0)}
                            {' · '}TDS ₹{formatInr(a.tds_amount ?? lineTds.tds)}
                            {' · '}Net ₹{formatInr(a.net_payable_amount ?? lineTds.net)}
                          </span>
                        </li>
                        );
                      })}
                    </ul>
                    ) : null}
                    {detail.status !== 'suspended' ? (
                      <p className="text-[11px] text-ink-muted">
                        Each line above is already claimed here. The next month only gets new earnings
                        (or leftover after this cutoff) — never this same amount again.
                      </p>
                    ) : null}
                    {detail.status !== 'suspended' && includedMonths.length > 1 ? (
                      <p className="text-[11px] text-ink-muted">
                        Management pays the combined net above. TDS 5% is taken once on the total gross
                        (₹{formatInr(detail.payout_breakdown?.tds_amount ?? detail.tds_amount ?? 0)}),
                        not by adding each month&apos;s TDS.
                      </p>
                    ) : null}
                  </div>
                  );
                })()}

                {(detail.payment_transactions || []).length > 0 ? (
                  <div className="rounded-ds-xl border border-line p-3 text-sm space-y-2">
                    <p className="text-[10px] font-bold uppercase text-ink-muted">
                      Payment transactions
                    </p>
                    <ul className="space-y-2">
                      {detail.payment_transactions.map((t) => (
                        <li
                          key={t.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-ds-lg border border-line bg-surface-muted/40 px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold tabular-nums text-ink">
                              ₹{formatInr(t.amount)} · {t.status}
                            </p>
                            <p className="text-xs text-ink-muted">
                              {t.payment_reference ? `Ref ${t.payment_reference} · ` : ''}
                              {t.processed_by ? `${t.processed_by} · ` : ''}
                              {t.paid_at ? formatWhen(t.paid_at) : formatWhen(t.created_at)}
                            </p>
                          </div>
                          {t.proof_url ? (
                            <AppButton
                              type="button"
                              variant="info"
                              icon="eye"
                              size="sm"
                              onClick={() =>
                                handleMediaView(t.proof_url, {
                                  onPreview: setMedia,
                                  title: 'Payment proof',
                                  subtitle: `Txn #${t.id}`,
                                })
                              }
                            >
                              Proof
                            </AppButton>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="rounded-ds-xl border border-line p-2.5 text-sm space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-0.5">
                    <p className="text-[10px] font-bold uppercase text-ink-muted">
                      Bank details (tap a value to copy)
                    </p>
                    <AppButton
                      type="button"
                      variant="info"
                      icon="copy"
                      size="sm"
                      onClick={() => copyAllPayoutDetails(detail)}
                      title="Copy all payout details"
                      aria-label="Copy all payout details"
                    >
                      {copiedField === 'Payout details' ? 'Copied' : 'All details'}
                    </AppButton>
                  </div>
                  {!detail.bank_details_ok && (
                    <div className="rounded-ds-lg border border-danger-100 bg-danger-50 p-2 mb-1 space-y-1">
                      <p className="text-xs font-bold text-danger-800">
                        Payment blocked — bank / UPI details missing
                      </p>
                      <p className="text-xs text-danger-800">
                        Contact the member (mobile / email / message) and ask them to save UPI or full bank details in Profile.
                        Record payment stays disabled until they update.
                      </p>
                      {(detail.phone || detail.user_email) ? (
                        <p className="text-[11px] font-semibold text-danger-800">
                          {detail.phone ? `Mobile: ${detail.phone}` : ''}
                          {detail.phone && detail.user_email ? ' · ' : ''}
                          {detail.user_email ? `Email: ${detail.user_email}` : ''}
                        </p>
                      ) : null}
                    </div>
                  )}
                  {[
                    {
                      key: 'Holder',
                      value: detail.bank_details?.account_holder_name,
                    },
                    {
                      key: 'Bank',
                      value: detail.bank_details?.bank_name,
                    },
                    {
                      key: 'Account',
                      value: fullAccountNumber(detail.bank_details),
                      mono: true,
                    },
                    {
                      key: 'IFSC',
                      value: detail.bank_details?.ifsc_code,
                      mono: true,
                    },
                    {
                      key: 'UPI',
                      value: detail.bank_details?.upi_id,
                      mono: true,
                    },
                  ].map((field) => {
                    const display = (field.value || '').trim() || '—';
                    const canCopy = Boolean((field.value || '').trim());
                    return (
                      <button
                        key={field.key}
                        type="button"
                        disabled={!canCopy}
                        onClick={() => copyText(field.key, field.value)}
                        className={`w-full flex items-center gap-2 rounded-lg border border-line bg-surface-muted/40 px-2 py-1 text-left transition-colors ${
                          canCopy ? 'hover:bg-brand-50 hover:border-brand-200 cursor-pointer' : 'opacity-70 cursor-default'
                        }`}
                        title={canCopy ? `Click to copy ${field.key}` : undefined}
                        aria-label={canCopy ? `Copy ${field.key}` : undefined}
                      >
                        <span className="w-14 flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                          {field.key}
                        </span>
                        <span
                          className={`min-w-0 flex-1 text-sm font-semibold text-ink truncate ${
                            field.mono ? 'font-mono tracking-wide' : ''
                          }`}
                        >
                          {display}
                        </span>
                        {canCopy ? (
                          <span
                            className={`flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded border ${
                              copiedField === field.key
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                                : 'border-line bg-white text-ink-muted'
                            }`}
                          >
                            {copiedField === field.key ? (
                              <CopiedIcon className="w-3.5 h-3.5" />
                            ) : (
                              <CopyIcon className="w-3.5 h-3.5" />
                            )}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                 
                </div>

                {detail.payment_proof_url ? (
                  <AppButton
                    type="button"
                    variant="info"
                    icon="eye"
                    onClick={() =>
                      handleMediaView(detail.payment_proof_url, {
                        onPreview: setMedia,
                        title: 'Payment proof',
                        subtitle: payoutMonthLabel(detail.payout_month),
                      })
                    }
                  >
                    View payment proof
                  </AppButton>
                ) : null}

                {(detail.open_concern || detail.member_remark || detail.concern_reason) ? (
                  <div className="rounded-ds-xl border border-danger-100 bg-danger-50 p-3 text-xs text-danger-800 space-y-1">
                    <p className="font-bold">
                      {detail.open_concern
                        ? `Payment issue · ${detail.open_concern.ticket_number} · ${detail.open_concern.status}`
                        : 'Member concern'}
                    </p>
                    {detail.open_concern?.reason || detail.concern_reason ? (
                      <p>{detail.open_concern?.reason || detail.concern_reason}</p>
                    ) : null}
                    <p>{detail.open_concern?.description || detail.member_remark}</p>
                    {(detail.open_concern?.actions || []).slice(0, 5).map((a) => (
                      <p key={a.id} className="text-[11px] text-danger-700/90">
                        {a.action_type}: {a.description || a.result}
                      </p>
                    ))}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2 border-t border-line pt-4">
                  {detail.can_recalculate ? (
                    <>
                      <AppButton type="button" variant="refresh" icon="refresh" disabled={saving} onClick={handleRecalculate}>
                        Refresh This Month
                      </AppButton>
                      <AppButton
                        type="button"
                        variant="refresh"
                        icon="sync"
                        disabled={saving}
                        onClick={() => handleRecalculateUser(detail.user_id)}
                      >
                        Update all months for this person
                      </AppButton>
                    </>
                  ) : null}
                  {detail.status === 'not_started' || detail.status === 'pending' || detail.status === 'approved' ? (
                    <AppButton
                      type="button"
                      variant="success"
                      icon="check"
                      disabled={saving || !detail.bank_details_ok || !detail.can_approve}
                      title={
                        !detail.bank_details_ok
                          ? 'Ask member to update bank / UPI details first'
                          : 'Approve for payout'
                      }
                      onClick={handleApprove}
                    >
                      Approve for payout
                    </AppButton>
                  ) : null}
                  {(detail.can_record_payment || detail.can_mark_paid
                    || ['yet_to_be_paid', 'partially_paid', 'approved', 'failed'].includes(detail.payment_status || detail.status))
                    && Number(detail.remaining_amount ?? 0) > 0 ? (
                    <div className="w-full space-y-2 rounded-ds-xl border-2 border-emerald-300 bg-emerald-50/40 p-4">
                      <p className="text-sm text-ink">
                        Still to send: <strong>₹{formatInr(detail.remaining_amount)}</strong>
                        {' · '}Already sent: ₹{formatInr(detail.paid_amount)}
                      </p>
                      <RecordPaymentCutoffNote detail={detail} />
                      {payAmount !== '' && Number(payAmount) > 0 ? (
                        <p className="text-sm font-semibold text-emerald-900">
                          ⬇️ After save, member points will go down by{' '}
                          {Number(walletDebitPreview(detail, payAmount)).toLocaleString('en-IN')}.
                        </p>
                      ) : (
                        <p className="text-sm text-ink-muted">
                          If you pay full ₹{formatInr(detail.remaining_amount)}, member points go down by{' '}
                          {Number(detail.unsettled_gross_points ?? 0).toLocaleString('en-IN')}.
                        </p>
                      )}
                      <label className={ds.label}>Payment amount (required)</label>
                      <input
                        type="number"
                        min="0.01"
                        max={Number(detail.remaining_amount ?? 0) || undefined}
                        step="0.01"
                        required
                        className={ds.input}
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        placeholder={`Remaining ₹${formatInr(detail.remaining_amount)}`}
                      />
                      <label className={ds.label}>Payment / reference ID (optional)</label>
                      <input
                        type="text"
                        className={ds.input}
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                        placeholder="Bank UTR / UPI ref"
                      />
                      <label className={ds.label}>Payment proof (required)</label>
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            proofInputRef.current?.click();
                          }
                        }}
                        onClick={() => proofInputRef.current?.click()}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setProofDragActive(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setProofDragActive(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setProofDragActive(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setProofDragActive(false);
                          acceptProofFile(e.dataTransfer.files?.[0] || null);
                        }}
                        className={`rounded-ds-xl border-2 border-dashed px-4 py-5 text-center transition-colors cursor-pointer ${
                          proofDragActive
                            ? 'border-brand-500 bg-brand-50'
                            : proofFile
                              ? 'border-emerald-400 bg-emerald-50'
                              : 'border-emerald-300 bg-white hover:border-brand-400 hover:bg-brand-50/40'
                        }`}
                      >
                        <p className="text-sm font-semibold text-ink">
                          {proofFile ? 'Proof selected' : 'Drag & drop payment proof here'}
                        </p>
                        <p className="text-xs text-ink-muted mt-1">
                          {PAYMENT_PROOF_HINT}
                        </p>
                        <span className="mt-3 inline-flex items-center justify-center rounded-ds-xl bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm">
                          {proofFile ? 'Replace file' : 'Choose file'}
                        </span>
                        <input
                          ref={proofInputRef}
                          type="file"
                          accept={PAYMENT_PROOF_ACCEPT}
                          className="hidden"
                          onChange={(e) => {
                            acceptProofFile(e.target.files?.[0] || null);
                            e.target.value = '';
                          }}
                        />
                      </div>
                      {proofFile ? (
                        <div className="flex items-center gap-3 rounded-ds-xl border border-line bg-surface px-3 py-2.5">
                          {proofPreviewUrl ? (
                            <img
                              src={proofPreviewUrl}
                              alt="Payment proof preview"
                              className="h-12 w-12 rounded-lg object-cover border border-line"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-line bg-surface-muted text-xs font-semibold text-ink-muted">
                              PDF
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-ink truncate">{proofFile.name}</p>
                            <p className="text-xs text-ink-muted">
                              {(proofFile.size / 1024).toFixed(0)} KB · ready to record
                            </p>
                          </div>
                          <AppButton
                            type="button"
                            variant="secondary"
                            icon="x"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProofFile(null);
                            }}
                          >
                            Remove
                          </AppButton>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-800">
                          Upload proof first — Record payment stays disabled until a file is selected.
                        </p>
                      )}
                      {detail.mark_paid_block_reason ? (
                        <p className="text-xs font-semibold text-danger-800">
                          {detail.mark_paid_block_reason}
                        </p>
                      ) : null}
                      <AppButton
                        type="button"
                        variant="success"
                        icon="pay"
                        disabled={
                          saving ||
                          !detail.bank_details_ok ||
                          !detail.can_mark_paid ||
                          !proofFile ||
                          payAmount === '' ||
                          Number(payAmount) <= 0 ||
                          Number(detail.remaining_amount ?? 0) <= 0 ||
                          Number(detail.amount ?? detail.gross_amount ?? 0) <= 0 ||
                          Number(payAmount) > Number(detail.remaining_amount ?? 0) + 0.001
                        }
                        title={
                          detail.mark_paid_block_reason
                            || (!detail.bank_details_ok
                              ? 'Ask member to update bank / UPI details first'
                              : Number(detail.remaining_amount ?? 0) <= 0
                                ? 'Nothing remaining to pay'
                                : !proofFile
                                  ? `Upload payment proof (${PAYMENT_PROOF_HINT}) first`
                                  : payAmount === '' || Number(payAmount) <= 0
                                    ? 'Enter the payment amount first'
                                  : Number(payAmount) > Number(detail.remaining_amount ?? 0) + 0.001
                                    ? `Amount cannot exceed remaining ₹${formatInr(detail.remaining_amount)}`
                                    : 'Record payment')
                        }
                        onClick={handleMarkPaid}
                      >
                        Record payment
                      </AppButton>
                    </div>
                  ) : detail.status === 'yet_to_be_paid' || detail.status === 'approved' ? (
                    <AppButton
                      type="button"
                      variant="success"
                      icon="pay"
                      disabled
                      title={detail.mark_paid_block_reason || 'Payment already done for this payout'}
                    >
                      Mark Paid
                    </AppButton>
                  ) : null}
                  {detail.can_reject ? (
                    <AppButton type="button" variant="danger" icon="x" disabled={saving} onClick={handleReject}>
                      {showAdminNotes ? 'Confirm reject' : 'Reject'}
                    </AppButton>
                  ) : null}
                  {showAdminNotes ? (
                    <div className="w-full space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <label className={ds.label}>Notes</label>
                        <AppButton
                          type="button"
                          variant="secondary"
                          size="sm"
                          icon="x"
                          onClick={() => {
                            setShowAdminNotes(false);
                            setAdminNote('');
                          }}
                        >
                          Hide notes
                        </AppButton>
                      </div>
                      <textarea
                        className={ds.input}
                        rows={3}
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        placeholder="Rejection / resolution / revert reason…"
                        autoFocus
                      />
                    </div>
                  ) : null}
                  {detail.can_review_concern ? (
                    <AppButton type="button" variant="warning" icon="review" disabled={saving} onClick={handleReviewConcern}>
                      Start review
                    </AppButton>
                  ) : null}
                  {detail.can_resolve_concern ? (
                    <AppButton type="button" variant="success" icon="resolve" disabled={saving} onClick={handleResolveConcern}>
                      Resolve concern
                    </AppButton>
                  ) : null}
                </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {finalizeModalStep > 0 ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-ds-2xl border border-line bg-surface shadow-ds-card">
            <div className="border-b border-line px-5 py-4">
              <h3 className="text-lg font-black text-ink">
                {finalizeModalStep === 1
                  ? `Close ${selectedMonthLabel}?`
                  : 'Last chance'}
              </h3>
              <p className="text-sm text-ink-muted mt-0.5">
                Date you picked: {cutoffDateLabel}
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
              {finalizeModalStep === 1 ? (
                <>
                  <p className="text-sm text-ink">
                    As per business, people usually select the <strong>last day of the month</strong>{' '}
                    (28th, 29th, 30th or 31st). Example: {previousMonthEndLabel} for last month.
                  </p>
                  {!cutoffIsMonthEnd ? (
                    <div className="rounded-ds-xl border-2 border-amber-400 bg-amber-50 px-3 py-3 text-sm text-amber-950 space-y-2">
                      <p className="font-bold">Are you sure you want this cutoff date?</p>
                      <p>
                        You picked <strong>{cutoffDateLabel}</strong>. That is{' '}
                        <strong>not</strong> month-end. It will lock{' '}
                        <strong>{selectedMonthLabel}</strong>, not another month.
                      </p>
                      <p>
                        If you meant month-end of last month, tap Cancel and pick{' '}
                        <strong>{previousMonthEndLabel}</strong>.
                        {monthEndIso && monthEndIso <= (cycleLockInfo.today || isoToday())
                          ? ` Month-end for ${selectedMonthLabel} is ${monthEndLabel}.`
                          : ''}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-ink">
                      You picked month-end <strong>{cutoffDateLabel}</strong>. That matches the
                      usual business date.
                    </p>
                  )}
                  <ul className="text-sm text-ink space-y-2">
                    <li>
                      ✅ Pay points earned till{' '}
                      <strong>
                        {cutoffIsToday
                          ? `${cutoffDateLabel}, at the time you confirm`
                          : `${cutoffDateLabel}, 11:59 PM`}
                      </strong>
                      . 5% tax is cut once (1 pt = ₹1).
                    </li>
                    <li>
                      ⏳ Points after that wait for the next month.
                    </li>
                    <li>
                      🔒 After you say yes, this date cannot be changed. There is no undo.
                    </li>
                    <li>
                      You can still close a different month that is not done yet (only one time per month).
                    </li>
                    <li>
                      Next month after {selectedMonthLabel}: <strong>{nextMonthAfterSelected}</strong>.
                    </li>
                  </ul>
                  <label className="flex items-start gap-2 rounded-ds-xl border-2 border-ink/20 bg-surface-muted/40 px-3 py-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={finalizeAckChecked}
                      onChange={(e) => setFinalizeAckChecked(e.target.checked)}
                    />
                    <span>
                      {cutoffIsMonthEnd
                        ? `I checked the date. I want cutoff ${cutoffDateLabel}. I cannot change it later.`
                        : `I checked the date. I really want ${cutoffDateLabel}, not the usual month-end date. I cannot change it later.`}
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <AppButton
                      type="button"
                      variant="success"
                      disabled={!finalizeAckChecked || saving}
                      onClick={() => setFinalizeModalStep(2)}
                    >
                      Continue
                    </AppButton>
                    <AppButton type="button" variant="secondary" icon="x" onClick={closeFinalizeModal}>
                      Cancel
                    </AppButton>
                  </div>
                </>
              ) : (
                <>
                  {!cutoffIsMonthEnd ? (
                    <div className="rounded-ds-xl border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                      Last check: this is <strong>{cutoffDateLabel}</strong>, not month-end
                      (usual example {previousMonthEndLabel}). Continue only if this date is correct.
                    </div>
                  ) : null}
                  <p className="text-sm text-ink">
                    You will close <strong>{selectedMonthLabel}</strong>.
                  </p>
                  <p className="text-sm text-ink">
                    Count points till{' '}
                    <strong>
                      {cutoffIsToday
                        ? `${cutoffDateLabel} at the time you confirm`
                        : `${cutoffDateLabel}, 11:59 PM`}
                    </strong>
                    .
                  </p>
                  <p className="text-sm font-semibold text-ink">
                    ❌ You cannot change this. You cannot undo this.
                  </p>
                  <p className="text-sm text-ink-muted">
                    Wrong date? Tap Go back and pick the date you really want.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <AppButton
                      type="button"
                      variant="success"
                      disabled={saving || !finalizeAckChecked}
                      onClick={submitLockedFinalize}
                    >
                      Yes, use {cutoffDateLabel}
                    </AppButton>
                    <AppButton
                      type="button"
                      variant="secondary"
                      disabled={saving}
                      onClick={() => setFinalizeModalStep(1)}
                    >
                      Go back
                    </AppButton>
                    <AppButton type="button" variant="secondary" icon="x" disabled={saving} onClick={closeFinalizeModal}>
                      Cancel
                    </AppButton>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showCutoffHelp ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setShowCutoffHelp(false)}
        >
          <div
            className="w-full max-w-lg rounded-ds-2xl border border-line bg-surface shadow-ds-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
              <div>
                <h3 className="text-lg font-black text-ink">Cutoff date</h3>
                <p className="text-sm text-ink-muted mt-0.5">How this date is used to pay members</p>
              </div>
              <AppButton type="button" variant="secondary" size="sm" icon="x" onClick={() => setShowCutoffHelp(false)}>
                Close
              </AppButton>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-ink">
              {cutoffIsToday ? (
                <p>
                  You picked <strong>{cutoffDateLabel}</strong> (today). Points count until the{' '}
                  <strong>time you confirm</strong>, not until midnight.
                </p>
              ) : (
                <p>
                  You picked <strong>{cutoffDateLabel}</strong>. Pay points earned till that day,{' '}
                  <strong>11:59 PM</strong>.
                </p>
              )}
              <p>
                Usual business cutoff is the <strong>last day of the month</strong> (28 / 29 / 30 / 31),
                example <strong>{previousMonthEndLabel}</strong>.
              </p>
              <p>5% tax is cut on that total (1 point = ₹1).</p>
              <p>
                After Finalize, that month is locked. You cannot pick another day in the same month.
                Next time, pick a date in the next month.
              </p>
              {selectedMonthLocked ? (
                <p className="rounded-ds-xl border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950">
                  {selectedMonthLabel} already has a cutoff
                  {selectedLockCutoffAt ? (
                    <>
                      {' '}until <strong>{selectedLockCutoffAt}</strong>
                    </>
                  ) : null}
                  .
                  {selectedLockChosenBy ? (
                    <>
                      {' '}Chosen by <strong>{selectedLockChosenBy}</strong>
                    </>
                  ) : null}
                  {selectedLockChosenAt ? <> on {selectedLockChosenAt}</> : null}.
                </p>
              ) : selectedBeforeFirstCycle ? (
                <p className="rounded-ds-xl border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950">
                  Payouts start from {formatYearMonthLong(firstCycleMonth)}. Pick a date in that month
                  or later.
                </p>
              ) : null}
              <div className="rounded-ds-xl border border-line bg-surface-muted/40 px-3 py-2">
                <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">Closed months</p>
                {(cycleLockInfo.locks || []).length > 0 ? (
                  <ul className="mt-1 space-y-1">
                    {(cycleLockInfo.locks || []).map((lock) => {
                      const month = cycleMonthFromCutoff(lock.cycle_month);
                      let shownCutoff = lock.cutoff_date;
                      if (!cutoffBelongsToMonth(shownCutoff, month)) {
                        const frozen = (rows || []).find(
                          (row) =>
                            row.status !== 'suspended' &&
                            row.amount_frozen &&
                            isSameCycleMonth(row.payout_month, month) &&
                            cutoffBelongsToMonth(row.cutoff_date || row.cutoff_at, month),
                        );
                        shownCutoff = frozen?.cutoff_date || frozen?.cutoff_at || shownCutoff;
                      }
                      return (
                        <li key={lock.id || lock.cycle_month}>
                          <span className="font-semibold">{formatYearMonthLong(lock.cycle_month)}</span>
                          {' · until '}
                          {lock.cutoff_at
                            ? formatAppDateTime(lock.cutoff_at)
                            : formatPayoutCutoffDate(shownCutoff)}
                          {lock.finalized_by ? ` · chosen by ${lock.finalized_by}` : ''}
                          {lock.finalized_at ? ` · ${formatAppDateTime(lock.finalized_at)}` : ''}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-1 text-ink-muted">
                    No month closed yet. After first Finalize, it will show here.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <MediaPreviewModal
        open={Boolean(media)}
        src={media?.src}
        title={media?.title}
        subtitle={media?.subtitle}
        onClose={() => setMedia(null)}
      />
    </div>
  );
}
